// src/coordinated-playbook/BindNRG2026CompChapter.js

import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../Security/firebase-config";
import "../../individual-playbook/General-Page.css";
import "../Coordinated-Playbook.css";
import "../../utils/ImageHoverEffect.css";
import { applyImageZoomEffect } from "../../utils/ImageHoverEffect";
import Footer from "../../components/core-components/Footer";
import GuideImage from "../../components/core-components/GuideImage";
import { resolveGuideAssetUrl } from "../../utils/resolveGuideAssetUrl";
import Highlight from "../../components/core-components/Highlight";
import Favorites from "../../components/core-components/Favorites";
import { useBlur } from "../../context/BlurContext";
import LazyVideo from "../../components/core-components/LazyVideo";
import MovementGuideModal from "../../components/core-components/MovementGuideModal";
import useAccordionDoubleClick from "../../hooks/useAccordionDoubleClick";

/**
 * ==========================================
 * BindNRG2026Comp MEDIA SOURCES
 * ==========================================
 * This now includes concrete example keys to demo the BindNRG2026Comp.
 */

const mediaSources = {
  /* =========================
   * DEFENSE EXAMPLE
   * ========================= */

  // Sub-accordion: defense-example-accordion
  "def-defense-example-accordion": {
    "example-overview": {
      label: "Example Overview",
      type: "image",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-ConU Comp 2 DEF Default Setup - GIF.gif"
    },
    "example-reaction1": {
      label: "Example Reaction 1",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-Fight.mp4",
      props: { ui: "minimal", loop: true }
    },
    "example-reaction2": {
      label: "Example Reaction 2",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-No-Reaction.mp4"
    }
  },

  /* =========================
    * DEFENSE Extremeties Default Setup FILLER 
    * ========================= */

  "def-fighting-for-extremities-default-setup": {
    "ffe-overview": {
        label: "Overview",
        type: "image",
        src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-ConU Comp 2 DEF Default Setup - GIF.gif"
    },
    "ffe-b-site": {
        label: "B-Site Protocols",
        type: "video",
        src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-B-Long-Trap.mp4"
    },
    "ffe-a-site": {
        label: "A-Site Protocols",
        type: "video",
        src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-Fast-Showers.mp4"
    }
  },

  /* =========================
   * ATTACK EXAMPLE
   * ========================= */

  // Sub-accordion: attack-example-accordion
  "atk-attack-example-accordion": {
    "attack-overview": {
      label: "Overview",
      type: "image",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-ConU Comp 2 ATK A-Rush - Tejo Ult-GIF.gif"
    },
    "attack-reaction1": {
      label: "Attack Reaction 1",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Rush - Tejo Ult.mp4"
    }
  },
 
  /* =========================
   * ATTACK A-Showers Default
   * ========================= */
  "atk-a-showers-default-accordion": {
      "a-showers-default-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Showers-Default-Phase-1.mp4",
      props: { ui: "minimal", loop: true }
      },
      "a-showers-default-a-rush": {
      label: "(Phase 2) A-Rush",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Showers-Default-Phase-2-A-Rush.mp4",
      props: { ui: "minimal", loop: true }
      },
      "a-showers-default-cut-noise-a-rehit": {
      label: "(Phase 2) Cut Noise A-reHit",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Showers-Default-Phase-2-A-Cut-Noise.mp4",
      props: { ui: "minimal", loop: true }
      },
      "a-showers-default-a-fake": {
      label: "(Phase 2) A-Fake",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Showers-Default-Phase-2-A-Fake.mp4",
      props: { ui: "minimal", loop: true }
      },
      "a-showers-default-b-hit": {
      label: "(Phase 2) B-Hit",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Showers-Default-Phase-2-B-Hit.mp4",
      props: { ui: "minimal", loop: true }
      },
      "a-showers-default-b-fake": {
      label: "(Phase 2) B-Fake",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Showers-Default-Phase-2-B-Fake.mp4",
      props: { ui: "minimal", loop: true }
      }
  },

  /* =========================
  * DEF A-SHORT PUSH
  * ========================= */
  "def-a-short-push": {
    "def-a-short-push-overview": {
      label: "Fight Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-Fight.mp4",
      props: { ui: "minimal", loop: true }
    },

    "def-a-short-push-showers": {
      label: "If ATK Reaction - Accelerate Showers",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-Fast-Showers.mp4",
      props: { ui: "minimal", loop: true }
    },

    "def-a-short-push-b-hit": {
      label: "If ATK Reaction - Accelerate B-Hit",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-Fast-B-general.mp4",
      props: { ui: "minimal", loop: true }
    },

    "def-a-short-push-no-response": {
      label: "If ATK Reaction - No Fast Response",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-No-Reaction.mp4",
      props: { ui: "minimal", loop: true }
    }
  },

  /* =========================
   * DEF Viper ULT A-Short
   * ========================= */
  "def-viper-ult-a-short": {
    "def-viper-ult-a-short-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-Ult-Setup-Viper-Ult-A-Short-General.mp4",
      props: { ui: "minimal", loop: true }
    },
    "def-viper-ult-a-short-a-pressure": {
      label: "A-Pressure Response",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-Ult-Setup-Viper-Ult-A-Short-A-Site-Protocols.mp4",
      props: { ui: "minimal", loop: true }
    },
    "def-viper-ult-a-short-b-pressure": {
      label: "B-Pressure Response",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-Ult-Setup-Viper-Ult-A-Short-B-Site-Protocols.mp4",
      props: { ui: "minimal", loop: true }
    }
  },

  /* =========================
  * DEF Ult Push Orbital Strike B-Fountain
  * ========================= */
  "def-ult-pushes-orbital-strike-b-long": {
    "def-ult-pushes-orbital-strike-b-long-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-Ult-Push-B-Fountain-Orbital-Strike.mp4",
      props: { ui: "minimal", loop: true }
    }
  },


  /* =========================
  * DEF B-LONG TRAP TEJO ULT
  * ========================= */
  "def-b-long-trap-tejo-ult": {
    "def-b-long-trap-tejo-ult-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-Ult-Trap-Tejo-B-Long.mp4",
      props: { ui: "minimal", loop: true },
      general: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "Waylay and Tejo Trap B-Long while Viper holds Hokkah space and Brim and Skye play on A-Side towards Showers.",
          "Having a set Team word indicating to activate the Trap such as: „SNAP“ is a good Protocol to ensure everyone across the map is on the same page of what is happening"
        ]
      },
      waylay: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "On Round start Waylay goes garden and Jiggles for the B-Long walk up and off contact throws the saturate B-Long to hit anyone half way up B-Long and coms the Keyword indicating to spring the Trap now (e.g. SNAP). Tejo will then combo his Ultimate from the bottom B-Long to Top B-Long with the Saturate.",
          "Then with the trap utility being thrown both Tejo and Waylay should fight B-Long to get the kills and hold the space."
        ]
      },
      skye: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "Skye stars the round in Showers and has Job of holding the showers space and calling for the timing for the Showers Crunch TP from B-Long in the event of an A-Hit. To be able to call for the Showers crunch in time and ensure the B-Long players are ready to TP to showers its good to get info on any potential Showers pressure as early as possible. Therefore, at the start of the round it's good for Skye to throw an Info flash outside showers, in order to convey the information and give the warning com to the B-Long players that they might need to give up the B-Long Trap and TP towards showers instead as early as possible."
        ]
      },
      tejo: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "On Round start Waylay goes garden and Jiggles for the B-Long walk up and off contact throws the Saturate Long to hit anyone half way up B-Long. Tejo will then combo his Ultimate from the bottom B-Long to Top B-Long with the Saturate. To ensure the right timing Tejo should have his iPad out and Ultimate preset and ready to send the moment Waylay calls for it with the Teams Keyword indicating to spring the trap (e.g. SNAP)",
          "Then with the trap utility being thrown both Tejo and Waylay should fight B-Long to get the kills and hold the space."
        ]
      },
      brimstone: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "Brim Plays on the weak A-Site in this setup and should play somewhere towards back, Trippel, Bench or Trash Can to simply spot for the attackers coming out from A-short.",
          "In reaction to an A-Hit Brim should provide the smoke on Default and molly to deny the plant to buy time.",
          "Placing that smoke on default makes it harder for the attackers out of A-Short to push Bench and crunch showers. It also allows Brim and Skye to setup a Showers/Triple crossfire, as well as allows for the defenders to spray through the smoke on any attackers plant attempt."
        ]
      },
      viper: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "Viper starts the round in Hokkah, ensuring that Waylay and Tejo can focus their attention on the trip B-Long.",
          "Seeing as in this Setup B-Site is the Strong side, Viper is  not required to hard fight for 1 lane to the same degree as when B-Site is the weak site. Thus, playing for info and stall with the molly before falling back is a perfectly viable option."
        ]
      }
    }
  },

  /* =========================
  * DEF B-LONG TEJO/WAYLAY TRAP
  * ========================= */
  "def-b-long-tejo-waylay-trap": {
    "def-b-long-tejo-waylay-trap-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-B-Long-Trap.mp4",
      props: { ui: "minimal", loop: true }
    }
  },

  /* =========================
   * ATTACK A-RUSH HEAVY A-SHORT
   * ========================= */
  "atk-a-rush-heavy-a-short": {
    "atk-a-rush-heavy-a-short-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Rush-Heavy-Short.mp4",
      props: { ui: "minimal", loop: true }
    }
  },

  /* =========================
   * ATTACK A-RUSH HEAVY SHOWERS
   * ========================= */
  "atk-a-rush-heavy-showers": {
    "atk-a-rush-heavy-showers-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Rush-Heavy-Showers.mp4",
      props: { ui: "minimal", loop: true }
    }
  },

  /* =========================
   * ATTACK TEJO ULT A-RUSH
   * ========================= */
  "atk-tejo-ult-a-rush": {
    "atk-tejo-ult-a-rush-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-A-Rush - Tejo Ult.mp4",
      props: { ui: "minimal", loop: true }
    }
  },

  /* =========================
   * ATTACK TEJO ULT B-RUSH
   * ========================= */
  "atk-tejo-ult-b-rush": {
    "atk-tejo-ult-b-rush-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-B-Rush-Tejo-Ult.mp4",
      props: { ui: "minimal", loop: true }
    }
  },
  /* =========================
   * ATTACK B-SPLIT
   * ========================= */
  "atk-b-split": {
    "atk-b-split-overview": {
      label: "Overview",
      type: "video",
      src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-B-Split.mp4",
      props: { ui: "minimal", loop: true }
    }
  },
};

/**
 * ==========================================
 * BindNRG2026Comp TEXT CONTENT
 * ==========================================
 * Text changes based on:
 * - sectionId
 * - selected media key
 * - selected role ("general", "agent1", ... "agent5")
 */

const ROLES = ["general", "agent1", "agent2", "agent3", "agent4", "agent5"];

const SECTION_ROLES = {
  "atk-a-showers-default-accordion": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "def-a-short-push": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "def-b-long-trap-tejo-ult": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "def-viper-ult-a-short": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "def-ult-pushes-orbital-strike-b-long": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
    "def-b-long-tejo-waylay-trap": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "atk-a-rush-heavy-a-short": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "atk-a-rush-heavy-showers": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "atk-tejo-ult-a-rush": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "atk-tejo-ult-b-rush": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
  "atk-b-split": [
    "general",
    "waylay",
    "skye",
    "tejo",
    "brimstone",
    "viper"
  ],
};

const ROLE_LABELS = {
  general: "General",
  agent1: "Agent 1",
  agent2: "Agent 2",
  agent3: "Agent 3",
  agent4: "Agent 4",
  agent5: "Agent 5",
  waylay: "Waylay",
  skye: "Skye",
  tejo: "Tejo",
  brimstone: "Brimstone",
  viper: "Viper",
  "b-hit-commit": "ATK react to the B-Crunch - committing B",
  "b-hit-tp": "ATK react to the B-Crunch - TPing A",
};

const DEF_A_SHORT_PUSH_B_HIT_ROLES = [
  "general",
  "b-hit-commit",
  "b-hit-tp"
];

const textContent = {
  "def-defense-example-accordion": {
    "example-overview": {
      general:
        "Defense / Overview / General: High-level explanation of the setup, default positions, and how the whole team should think about this round.",
      agent1:
        "Defense / Overview / Agent 1: Focus on anchor responsibilities – where to play, what utility to hold, and how to survive the first wave.",
      agent2:
        "Defense / Overview / Agent 2: Describe how this agent supports with utility, re-swings, and off-angles after first contact.",
      agent3:
        "Defense / Overview / Agent 3: Explain lurk/rotate timings and when this agent should leave site or push for info.",
      agent4:
        "Defense / Overview / Agent 4: Outline how this agent trades, holds crossfires, and coordinates with Agent 1.",
      agent5:
        "Defense / Overview / Agent 5: Clarify flex roles, plug gaps in the setup, and watch for mid-round adaptations."
    },
    "example-reaction1": {
      general:
        "Defense / Reaction 1 / General: How the team should react when the enemy hits fast – who fights, who delays, who rotates.",
      agent1:
        "Defense / Reaction 1 / Agent 1: When you hear fast exec, drop your early utility, fall to safe positions, and call for help quickly.",
      agent2:
        "Defense / Reaction 1 / Agent 2: Use your utility to delay the push and create crossfires with Agent 1 instead of solo peeking.",
      agent3:
        "Defense / Reaction 1 / Agent 3: Be ready to flank or fast rotate once you’ve confirmed the hit – timing is more important than kills.",
      agent4:
        "Defense / Reaction 1 / Agent 4: Hold a late off-angle, don’t overfight – let your utility and teammates stall first.",
      agent5:
        "Defense / Reaction 1 / Agent 5: Watch for lurks and cut off rotations so your team doesn’t get pinched while defending."
    },
    "example-reaction2": {
      general:
        "Defense / Reaction 2 / General: Game plan for slow defaults – how to manage utility and information over the full round.",
      agent1:
        "Defense / Reaction 2 / Agent 1: Resist the urge to overpeek; save key utility for the final 20 seconds of the round.",
      agent2:
        "Defense / Reaction 2 / Agent 2: Use small pieces of utility to check space instead of giving up your full kit early.",
      agent3:
        "Defense / Reaction 2 / Agent 3: Probe the map safely for information, but always have an exit route and a teammate nearby.",
      agent4:
        "Defense / Reaction 2 / Agent 4: Anchor a key choke and communicate what you see so the team can adjust rotates.",
      agent5:
        "Defense / Reaction 2 / Agent 5: Fill gaps in the setup – if someone floats for info, make sure the site is still covered."
    }
  },

  "atk-attack-example-accordion": {
    "attack-overview": {
      general:
        "Attack / Overview / General: Basic game plan for the hit – where everyone starts, which areas you want control of, and your win condition.",
      agent1:
        "Attack / Overview / Agent 1: Entry responsibilities – first contact, clearing common angles, and calling site conditions.",
      agent2:
        "Attack / Overview / Agent 2: Second in – trade frags, clear deeper angles, and stabilize site control after the entry.",
      agent3:
        "Attack / Overview / Agent 3: Lurk or map control role – manage timings so the team doesn’t get flanked.",
      agent4:
        "Attack / Overview / Agent 4: Support with flashes/smokes to enable the front line and secure safe plants.",
      agent5:
        "Attack / Overview / Agent 5: Backup utility and post-plant anchor – think about lineups and safe positions."
    },
    "attack-reaction1": {
      general:
        "Attack / Reaction 1 / General: How to adapt when the defense fights early – when to commit, reset, or pivot.",
      agent1:
        "Attack / Reaction 1 / Agent 1: If you’re denied space early, don’t force – call to fall back and re-hit with better utility.",
      agent2:
        "Attack / Reaction 1 / Agent 2: Trade carefully; don’t overextend if utility is gone and your entry has fallen.",
      agent3:
        "Attack / Reaction 1 / Agent 3: Look for punishing over-aggression on the other side of the map after early contact.",
      agent4:
        "Attack / Reaction 1 / Agent 4: Save some utility for the re-commit so your team isn’t dry swinging into utility.",
      agent5:
        "Attack / Reaction 1 / Agent 5: Communicate the bigger picture – whether you have numbers, map control, or time advantage."
    }
  }
};

void textContent;


const detailedSectionContent = {
  "def-a-short-push": {
    "def-a-short-push-overview": {
      general: {
        title: "Video Script Defense A-Short Push",
        paragraphs: [
          "This A-Short push is good to do in rounds where you are in a disadvantage, such as on eco or when vs big ults."
        ]
      },
      waylay: {
        title: "Video Script Defense A-Short Push",
        paragraphs: [
          "Off Barrier drop Waylay saturate‘s the right side of A-Short before placing the refract down and double dashing onto the A-short bench, as Skye flashes the space.",
          "After killing the players close caught by the saturate Waylay peeks further into market, capitalizing of the the players stunned by Tejo in the non Salvo‘d area."
        ]
      },
      skye: {
        title: "Video Script Defense A-Short Push",
        paragraphs: [
          "Off barrier drop Skye flashes the right side of A-Short and pushes down with Waylay to play for trades."
        ]
      },
      tejo: {
        title: "Video Script Defense A-Short Push",
        paragraphs: [
          "As the barrier drops Tejo sends two Salvo‘s into Market. Right afterwards Tejo uses the stun lineup to stun anyone in the market space that is not Salvo‘d.",
          "After this Tejo holds for any late swing out of the Brim smoke."
        ]
      },
      brimstone: {
        title: "Video Script Defense A-Short Push",
        paragraphs: [
          "On Round start Brim smokes the left Side of A-Short and then mollies the smoke.",
          "After doing so Brim Switches his focus onto any potential fast attackers reaction out of Showers."
        ]
      },
      viper: {
        title: "Video Script Defense A-Short Push",
        paragraphs: [
          "In this A-Short push Viper plays the passive info role towards B, playing to stall any Hokkah push with her molly and listening for any attackers reaction to the A-Short push."
        ]
      }
    }
  },
  "def-viper-ult-a-short": {
    "def-viper-ult-a-short-overview": {
      general: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Viper throws her Poisson Cloud as a One-Way for showers, her Wall for B-Site and Ults down A-Short.",
          "Waylay and Skye start the round B-Long with a deep B-Long smoke, while Brim Holds their Hokkah from Site and Tejo starts towards Showers."
        ]
      },
      waylay: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "On Round start Brim Smokes deep B-Long. With this Waylay cross to the TP side of B-Long and Skye holds towards Garden.",
          "In the event of B-Long pressure Waylay and Skye can then use the Saturate and Flashes to hold and fight for the space.",
          "In the event of A-pressure depending on the information in the round Waylay can asked to be dogged through the TP, take B-Fountain space with a flash or fast rotate through defenders spawn."
        ]
      },
      skye: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "On Round start Brim Smokes deep B-Long. With this Waylay cross to the TP side of B-Long and Skye holds towards Garden.",
          "In the event of B-Long pressure Waylay and Skye can then use the Saturate and Flashes to hold and fight for the space.",
          "In the event of A-pressure, Brim will be the first rotator through defenders spawn. At the same time depending on the information in the round Waylay can asked to be dogged through the TP, take B-Fountain space with a flash or fast rotate through defenders spawn.",
          "in the event that Waylay took the TP or rotated through defenders spawn Skye remains on B-Site as the anchor and is free to use utility towards B-Fountain later on in the round to check if it A-pressure was an A-Fake."
        ]
      },
      tejo: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Tejo starts the round towards A-showers jiggling the Showers space from the Site-Showers choke. Off contact Tejo falls back towards Tripple and asks for the Showers One way from Viper.",
          "If forced of Tejo should fall back towards lamps or even into the viper Ult and stall the plant on A-with his utility.",
          "If Tejo falls back into the Viper Ult, Tejo should communicate to viper that he will watch her back, while Viper focuses on any potential late lurk from A-Short."
        ]
      },
      brimstone: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Brim starts the round by Smoking B-Long. Brim then holds an off angle on Hokkah from site, thereby holding Waylay and Skye’s back who are playing B-Long. Good off angle options are Top Gen or front Gen are good options to do so.",
          "In the event of any Hokkah pressure Brim takes his off angle shot before instantly falling off the angle and asking for the Viper Wall to come up and if needed smokes it himself.",
          "In the event that the Hit through Hokkah seems to continue throwing the molly below Hokkah in addition to repositioning towards B-Pocket can be advantageous.",
          "The Molly below Hokkah helps against any attackers Jumping out of Hokkah and the repositioning towards B-Pocket is beneficial, as Brim then is in a position where the utility from Hokkah like a Raze pain shell or Viper Molly cannot flush him out. Brim is able to play there as he does not need to worry about his B-Long as Waylay and Skye are holding it and",
          "In the event of A-pressure Brim is the first rotator. Brim should rotate through defenders spawn and clearly communicate to Waylay and Skye that he is no longer holding their Hokkah."
        ]
      },
      viper: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "In the Pre-round Viper throws the one-way orb on Showers, then from Lamps Walls Hokkah and Garden.",
          "On Barrier drop, Viper then Ults A-Short.",
          "In the event of an A-Hit through showers then brut forcing their Way onto site, and Viper is solo in her Ult she should push forward towards A-Short actively looking for the late A-Short lurker, as its safe for Viper to assume that if the attackers went for a heavy Showers execute there is a maximum of 1 or 2 defenders A-Short and its better for Viper to look to try and fight that weak side than wait in her Ult to be crunched from both site and A-Short.",
          "In the event of B-pressure and it seeming like a B-Hit Viper remains A as the Site Anchor."
        ]
      }
    },

    "def-viper-ult-a-short-a-pressure": {
      general: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Viper throws her Poisson Cloud as a One-Way for showers, her Wall for B-Site and Ults down A-Short.",
          "Waylay and Skye start the round B-Long with a deep B-Long smoke, while Brim Holds their Hokkah from Site and Tejo starts towards Showers."
        ]
      },
      waylay: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "On Round start Brim Smokes deep B-Long. With this Waylay cross to the TP side of B-Long and Skye holds towards Garden.",
          "In the event of B-Long pressure Waylay and Skye can then use the Saturate and Flashes to hold and fight for the space.",
          "In the event of A-pressure depending on the information in the round Waylay can asked to be dogged through the TP, take B-Fountain space with a flash or fast rotate through defenders spawn."
        ]
      },
      skye: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "On Round start Brim Smokes deep B-Long. With this Waylay cross to the TP side of B-Long and Skye holds towards Garden.",
          "In the event of B-Long pressure Waylay and Skye can then use the Saturate and Flashes to hold and fight for the space.",
          "In the event of A-pressure, Brim will be the first rotator through defenders spawn. At the same time depending on the information in the round Waylay can asked to be dogged through the TP, take B-Fountain space with a flash or fast rotate through defenders spawn.",
          "in the event that Waylay took the TP or rotated through defenders spawn Skye remains on B-Site as the anchor and is free to use utility towards B-Fountain later on in the round to check if it A-pressure was an A-Fake."
        ]
      },
      tejo: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Tejo starts the round towards A-showers jiggling the Showers space from the Site-Showers choke. Off contact Tejo falls back towards Tripple and asks for the Showers One way from Viper.",
          "If forced of Tejo should fall back towards lamps or even into the viper Ult and stall the plant on A-with his utility.",
          "If Tejo falls back into the Viper Ult, Tejo should communicate to viper that he will watch her back, while Viper focuses on any potential late lurk from A-Short."
        ]
      },
      brimstone: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Brim starts the round by Smoking B-Long. Brim then holds an off angle on Hokkah from site, thereby holding Waylay and Skye’s back who are playing B-Long. Good off angle options are Top Gen or front Gen are good options to do so.",
          "In the event of any Hokkah pressure Brim takes his off angle shot before instantly falling off the angle and asking for the Viper Wall to come up and if needed smokes it himself.",
          "In the event that the Hit through Hokkah seems to continue throwing the molly below Hokkah in addition to repositioning towards B-Pocket can be advantageous.",
          "The Molly below Hokkah helps against any attackers Jumping out of Hokkah and the repositioning towards B-Pocket is beneficial, as Brim then is in a position where the utility from Hokkah like a Raze pain shell or Viper Molly cannot flush him out. Brim is able to play there as he does not need to worry about his B-Long as Waylay and Skye are holding it and",
          "In the event of A-pressure Brim is the first rotator. Brim should rotate through defenders spawn and clearly communicate to Waylay and Skye that he is no longer holding their Hokkah."
        ]
      },
      viper: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "In the Pre-round Viper throws the one-way orb on Showers, then from Lamps Walls Hokkah and Garden.",
          "On Barrier drop, Viper then Ults A-Short.",
          "In the event of an A-Hit through showers then brut forcing their Way onto site, and Viper is solo in her Ult she should push forward towards A-Short actively looking for the late A-Short lurker, as its safe for Viper to assume that if the attackers went for a heavy Showers execute there is a maximum of 1 or 2 defenders A-Short and its better for Viper to look to try and fight that weak side than wait in her Ult to be crunched from both site and A-Short.",
          "In the event of B-pressure and it seeming like a B-Hit Viper remains A as the Site Anchor."
        ]
      }
    },

    "def-viper-ult-a-short-b-pressure": {
      general: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Viper throws her Poisson Cloud as a One-Way for showers, her Wall for B-Site and Ults down A-Short.",
          "Waylay and Skye start the round B-Long with a deep B-Long smoke, while Brim Holds their Hokkah from Site and Tejo starts towards Showers."
        ]
      },
      waylay: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "On Round start Brim Smokes deep B-Long. With this Waylay cross to the TP side of B-Long and Skye holds towards Garden.",
          "In the event of B-Long pressure Waylay and Skye can then use the Saturate and Flashes to hold and fight for the space.",
          "In the event of A-pressure depending on the information in the round Waylay can asked to be dogged through the TP, take B-Fountain space with a flash or fast rotate through defenders spawn."
        ]
      },
      skye: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "On Round start Brim Smokes deep B-Long. With this Waylay cross to the TP side of B-Long and Skye holds towards Garden.",
          "In the event of B-Long pressure Waylay and Skye can then use the Saturate and Flashes to hold and fight for the space.",
          "In the event of A-pressure, Brim will be the first rotator through defenders spawn. At the same time depending on the information in the round Waylay can asked to be dogged through the TP, take B-Fountain space with a flash or fast rotate through defenders spawn.",
          "in the event that Waylay took the TP or rotated through defenders spawn Skye remains on B-Site as the anchor and is free to use utility towards B-Fountain later on in the round to check if it A-pressure was an A-Fake."
        ]
      },
      tejo: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Tejo starts the round towards A-showers jiggling the Showers space from the Site-Showers choke. Off contact Tejo falls back towards Tripple and asks for the Showers One way from Viper.",
          "If forced of Tejo should fall back towards lamps or even into the viper Ult and stall the plant on A-with his utility.",
          "If Tejo falls back into the Viper Ult, Tejo should communicate to viper that he will watch her back, while Viper focuses on any potential late lurk from A-Short."
        ]
      },
      brimstone: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "Brim starts the round by Smoking B-Long. Brim then holds an off angle on Hokkah from site, thereby holding Waylay and Skye’s back who are playing B-Long. Good off angle options are Top Gen or front Gen are good options to do so.",
          "In the event of any Hokkah pressure Brim takes his off angle shot before instantly falling off the angle and asking for the Viper Wall to come up and if needed smokes it himself.",
          "In the event that the Hit through Hokkah seems to continue throwing the molly below Hokkah in addition to repositioning towards B-Pocket can be advantageous.",
          "The Molly below Hokkah helps against any attackers Jumping out of Hokkah and the repositioning towards B-Pocket is beneficial, as Brim then is in a position where the utility from Hokkah like a Raze pain shell or Viper Molly cannot flush him out. Brim is able to play there as he does not need to worry about his B-Long as Waylay and Skye are holding it and",
          "In the event of A-pressure Brim is the first rotator. Brim should rotate through defenders spawn and clearly communicate to Waylay and Skye that he is no longer holding their Hokkah."
        ]
      },
      viper: {
        title: "DEF Viper Ult A-Short",
        paragraphs: [
          "In the Pre-round Viper throws the one-way orb on Showers, then from Lamps Walls Hokkah and Garden.",
          "On Barrier drop, Viper then Ults A-Short.",
          "In the event of an A-Hit through showers then brut forcing their Way onto site, and Viper is solo in her Ult she should push forward towards A-Short actively looking for the late A-Short lurker, as its safe for Viper to assume that if the attackers went for a heavy Showers execute there is a maximum of 1 or 2 defenders A-Short and its better for Viper to look to try and fight that weak side than wait in her Ult to be crunched from both site and A-Short.",
          "In the event of B-pressure and it seeming like a B-Hit Viper remains A as the Site Anchor."
        ]
      }
    },
  },
  "def-b-long-trap-tejo-ult": {
    "def-b-long-trap-tejo-ult-overview": {
      general: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "Waylay and Tejo Trap B-Long while Viper holds Hokkah space and Brim and Skye play on A-Side towards Showers.",
          "Having a set Team word indicating to activate the Trap such as: „SNAP“ is a good Protocol to ensure everyone across the map is on the same page of what is happening"
        ]
      },
      waylay: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "On Round start Waylay goes garden and Jiggles for the B-Long walk up and off contact throws the saturate B-Long to hit anyone half way up B-Long and coms the Keyword indicating to spring the Trap now (e.g. SNAP). Tejo will then combo his Ultimate from the bottom B-Long to Top B-Long with the Saturate.",
          "Then with the trap utility being thrown both Tejo and Waylay should fight B-Long to get the kills and hold the space."
        ]
      },
      skye: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "Skye stars the round in Showers and has Job of holding the showers space and calling for the timing for the Showers Crunch TP from B-Long in the event of an A-Hit. To be able to call for the Showers crunch in time and ensure the B-Long players are ready to TP to showers its good to get info on any potential Showers pressure as early as possible. Therefore, at the start of the round it's good for Skye to throw an Info flash outside showers, in order to convey the information and give the warning com to the B-Long players that they might need to give up the B-Long Trap and TP towards showers instead as early as possible."
        ]
      },
      tejo: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "On Round start Waylay goes garden and Jiggles for the B-Long walk up and off contact throws the Saturate Long to hit anyone half way up B-Long. Tejo will then combo his Ultimate from the bottom B-Long to Top B-Long with the Saturate. To ensure the right timing Tejo should have his iPad out and Ultimate preset and ready to send the moment Waylay calls for it with the Teams Keyword indicating to spring the trap (e.g. SNAP)",
          "Then with the trap utility being thrown both Tejo and Waylay should fight B-Long to get the kills and hold the space."
        ]
      },
      brimstone: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "Brim Plays on the weak A-Site in this setup and should play somewhere towards back, Trippel, Bench or Trash Can to simply spot for the attackers coming out from A-short.",
          "In reaction to an A-Hit Brim should provide the smoke on Default and molly to deny the plant to buy time.",
          "Placing that smoke on default makes it harder for the attackers out of A-Short to push Bench and crunch showers. It also allows Brim and Skye to setup a Showers/Triple crossfire, as well as allows for the defenders to spray through the smoke on any attackers plant attempt."
        ]
      },
      viper: {
        title: "DEF B-Long Trap Tejo Ult",
        paragraphs: [
          "Viper starts the round in Hokkah, ensuring that Waylay and Tejo can focus their attention on the trip B-Long.",
          "Seeing as in this Setup B-Site is the Strong side, Viper is  not required to hard fight for 1 lane to the same degree as when B-Site is the weak site. Thus, playing for info and stall with the molly before falling back is a perfectly viable option."
        ]
      }
    }
  },
  "def-b-long-tejo-waylay-trap": {
    "def-b-long-tejo-waylay-trap-overview": {
      general: {
        title: "DEF B-Long Tejo/Waylay Trap",
        paragraphs: [
          "Overview video for the DEF B-Long Tejo/Waylay Trap."
        ]
      },
      waylay: {
        title: "DEF B-Long Tejo/Waylay Trap",
        paragraphs: [
          "Waylay-specific notes for the DEF B-Long Tejo/Waylay Trap can be added here."
        ]
      },
      skye: {
        title: "DEF B-Long Tejo/Waylay Trap",
        paragraphs: [
          "Skye-specific notes for the DEF B-Long Tejo/Waylay Trap can be added here."
        ]
      },
      tejo: {
        title: "DEF B-Long Tejo/Waylay Trap",
        paragraphs: [
          "Tejo-specific notes for the DEF B-Long Tejo/Waylay Trap can be added here."
        ]
      },
      brimstone: {
        title: "DEF B-Long Tejo/Waylay Trap",
        paragraphs: [
          "Brimstone-specific notes for the DEF B-Long Tejo/Waylay Trap can be added here."
        ]
      },
      viper: {
        title: "DEF B-Long Tejo/Waylay Trap",
        paragraphs: [
          "Viper-specific notes for the DEF B-Long Tejo/Waylay Trap can be added here."
        ]
      }
    }
  },
  "def-ult-pushes-orbital-strike-b-long": {
    "def-ult-pushes-orbital-strike-b-long-overview": {
      general: {
        title: "DEF Ult Pushes Orbital Strike B-Long",
        paragraphs: [
          "Waylay dashes down B-Long with a Skye flash and off her hitting attackers with her saturate Brim sends his Orbital Strike."
        ]
      },
      waylay: {
        title: "DEF Ult Pushes Orbital Strike B-Long",
        paragraphs: [
          "Off barrier drop Waylay places her refract in Garden before sprinting down B-Long and Dashing high up towards fountain (with the help of a Skye flash) throwing her Saturate on any attackers she sees there before instantly refracting.",
          "If the slow hits Waylay should use a key trigger word previously agreed on to signal for Brim to send his Orbital Strike down on the hindered Attacker. Good example of such key words are: „Slowed“.",
          "If Waylay sees no one towards B and the A-players are calling an A-Hit waylay should decide not to refract and keep flanking instead. On the flank asking for a smoke from Brim to sneak past any trip if necessary."
        ]
      },
      skye: {
        title: "DEF Ult Pushes Orbital Strike B-Long",
        paragraphs: [
          "On round start Skye flashes down B-Long for Waylay to follow up on with her dash to Saturate any attackers B-Fountain which Brim combos with his Orbital Strike.",
          "If there is nothing towards B and it seems like an A-Hit Waylay should flank. If waylay flanks Skye and Brim should not take the TP as that will make the attackers more worrisome of the potential flank."
        ]
      },
      tejo: {
        title: "DEF Ult Pushes Orbital Strike B-Long",
        paragraphs: [
          "Tejo starts the Round towards bench/tripple, while Viper holds the showers space.",
          "In the event of an A-Hit, Tejo asks for the Viper to put up her Poison Cloud that covers Default. Then off of the spike plant noise, Tejo stuns throw the orb and calls for Brim to combo his orbital strike with it. If Brim is out of range Tejo can combo his Salvo‘s instead."
        ]
      },
      brimstone: {
        title: "DEF Ult Pushes Orbital Strike B-Long",
        paragraphs: [
          "Brim starts the round in Hokkah. Then waits with his Ult out for Waylay to push up B-Long with the Skye flash and make the trigrr com. This trigger com is a key trigger word previously agreed on to signal for Brim to send his Orbital Strike down on the hindered Attacker. Good example of such key words are: „Slowed“.",
          "If Waylay sees no one and the A-players call an A-Hit, Waylay will flank and if there is a flank trip ask for a smoke, so it is important for Brim to remain in range to smoke the trip, as well as send his ult onto A-default. As on spike plant tejo will stun the planter and calls for Brims Orbital strike in combination."
        ]
      },
      viper: {
        title: "DEF Ult Pushes Orbital Strike B-Long",
        paragraphs: [
          "In the pre-round Viper an A-Showers/Retake Wall and her Poison Cloud is across the A-Default.",
          "Viper then plays A-Site with Tejo, starting the round towards showers holding the space with her Molly and Wall if needed. While Tejo watches her back and A-short from Bench/Tripple playing to stun the planter behind the protection of the Poison Cloud being up in an effort to Combo the Tejo stun with Brims Orbital Strike."
        ]
      }
    }
  },
  "atk-a-rush-heavy-a-short": {
    "atk-a-rush-heavy-a-short-overview": {
      general: {
        title: "ATK A-Rush Heavy A-Short",
        paragraphs: [
          "This A-Split push is good to do when the defenders don’t tend to fight for Showers to heavily and prefer playing towards bench, back site and lamps."
        ]
      },
      waylay: {
        title: "ATK A-Rush Heavy A-Short",
        paragraphs: [
          "On round start Waylay holds for any potential attacker walking down A-short into cubby. Before then scaling up A-short behind the Viper wall and dashing onto site after the Tejo drone has provided info.",
          "Waylay has a few options as to where to dash for the execute such as:",
          "If the Tejo drone has cleared front U-Haul and pocket dashing Top Lamps Box is the preferred pathing as this allows Waylay crunch any bench players with her teammates in showers, as well as gives her the option to peak into back site or even jump onto truck in any potential back site fight.",
          "In the event the showers players are completely stalled out, it is on waylay (and either Tejo or Brim) to try and crunch the defenders in showers from Site."
        ],
        bullets: [
          "Pocket",
          "Lamps",
          "Top Lamps Box"
        ],
        bulletInsertAfterParagraph: 1
      },
      skye: {
        title: "ATK A-Rush Heavy A-Short",
        paragraphs: [
          "On round start Skye throws the one-way flash into showers for herself and Viper to follow up.",
          "With the Skye flash and Tejo Salvo in most cases it should be quite simple for Skye & Viper to take showers. However, if they get stalled out they should ask for the A-short players to crunch showers from Site.",
          "After Syke and Viper have dice take the showers space, Skye should dog onto site for the execute."
        ]
      },
      tejo: {
        title: "ATK A-Rush Heavy A-Short",
        paragraphs: [
          "On round start Tejo salvo‘s the defenders A-Showers entrance. Here it’s important that Tejo throws his salvo deeper into showers than normally, as done e.g. on A-split heavy A-Showers. This distinction is made because the regular Salvo is more so used to trap any defender in showers, while this deeper salvo is more effective in discouraging the push into showers by a solo defender in the first place.",
          "After throwing the Salvo Tejo drones up A-Short. Hereby, the drone pathing should first clear A-Cubby before clearing A-Pocket and then going into lamps for Brim to follow up.",
          "If the showers players get stalled out its on Tejo and Waylay to crunch the defenders in showers from site. If for some reason Brim already followed up Waylay to crunch showers then it becomes Tejo‘s job to hold their back and ensure they don’t get backstabbed from a player peaking out of the back site smokes."
        ]
      },
      brimstone: {
        title: "ATK A-Rush Heavy A-Short",
        paragraphs: [
          "On round start Brim goes A-short from Market side and follows up the Tejo drone into Lamps.",
          "In rounds where the defenders are on an Eco if the drone spots anyone in Hokkah it is better for Brim to simply put her orb up and hold Hokkah rather than take the space and risk a disadvantage fight versus a close range gun",
          "For the execute it’s important for Brim to smoke Heaven, plus the default smokes.",
          "The Heaven smoke allows for Waylay to dash up top A-Lamps box or top pocket box without being worried about being shot from Heaven."
        ]
      },
      viper: {
        title: "ATK A-Rush Heavy A-Short",
        paragraphs: [
          "On this A-Split viper goes showers and follows up Skye‘s one way flash into showers. If stalled out or encountering heavy pressure Viper is free to use her molly to help fight for Showers space.",
          "In the event of being stalled out of showers, Waylay and Tejo will crunch showers from Site."
        ]
      }
    }
  },
  "atk-a-rush-heavy-showers": {
    "atk-a-rush-heavy-showers-overview": {
      general: {
        title: "ATK A-Rush Heavy Showers",
        paragraphs: [
          "This A-Split variation is good to use when the defenders tend to fight heavier towards showers nd give A-Short space fairly easily.",
          "On this A-Split Waylay, Brim and Skye hit A-Site through showers, while Tejo nd Viper hit A-site through A-Short."
        ]
      },
      waylay: {
        title: "ATK A-Rush Heavy Showers",
        paragraphs: [
          "On round start Waylay and Brim swing wide with the Stim Beacon and a Skye one way flash to fight showers and hold for any defenders swinging out of showers. Meanwhile, Skye scales up the left side of the A-Showers to dog from within showers, Waylay and Brim then follow up this Skye dog for the Site hit.",
          "In the event that a defender TPs showers before Waylay, Brim and Skye get into showers, it is on Brim to hold for the defender in TP. If the three have already gotten into showers and started the Site-Hit it is on the player the furthest back to turn around and hold for the defender who TP‘d (keeping in mind this should essentially never be waylay)."
        ]
      },
      skye: {
        title: "ATK A-Rush Heavy Showers",
        paragraphs: [
          "On Barrier drop, Skye throws her one way flash into showers. After this Skye quickly scales up the left side of A-showers while Brim and Waylay hold her. From here Skye dogs Waylay and Brim onto site.",
          "In the event that a defender TPs showers before Waylay, Brim and Skye get into showers, it is on Brim to hold for the defender in TP. If the three have already gotten into showers and started the Site-Hit it is on the player the furthest back to turn around and hold for the defender who TP‘d (keeping in mind this should essentially never be Waylay)."
        ]
      },
      tejo: {
        title: "ATK A-Rush Heavy Showers",
        paragraphs: [
          "On barrier drop Tejo Salvo‘s the defenders entrance into showers. This salvo, forces any defender who wanted to play in showers into the hard choice of either swinging past the salvo into showers before it lands, in which case the defender is then stuck in showers and has to deal with Waylay, Skye and Brim or deciding to change the initial plan and not fight for showers at the start of the round after all.",
          "Immediately after sending the Salvo Tejo peaks A-Short clearing for the defenders cross into A-cubby before then asking Viper to walk up and droning up A-Short and on the drone pathing clearing A-Cubby, A-pocket and Lamps for Viper to follow up."
        ]
      },
      brimstone: {
        title: "ATK A-Rush Heavy Showers",
        paragraphs: [
          "On Barrier drop, Brim Stim Beacons and swings wide with Waylay. Then Brim and Waylay hold for the showers peak, while Skye scales up the left side of the Showers. Then Brim and Waylay follow up the Skye dog on the site hit.",
          "In the event that a defender TPs showers before Waylay, Brim and Skye get into showers, it is on Brim to hold for the defender in TP. If the three have already gotten into showers and started the Site-Hit it is on the player the furthest back to turn around and hold for the defender who TP‘d (keeping in mind this should essentially never be Waylay)."
        ]
      },
      viper: {
        title: "ATK A-Rush Heavy Showers",
        paragraphs: [
          "On this A-Split variation, Viper starts the round in Market and then follows up the Tejo drone A-Short and should end by taking the Hokkah space. In rounds where the defenders are on an Eco if the drone spots anyone in Hokkah it is better for Viper to simply put her orb up and hold Hokkah rather than take the space and risk a disadvantage fight versus a close range gun."
        ]
      }
    }
  },
  "atk-tejo-ult-a-rush": {
    "atk-tejo-ult-a-rush-overview": {
      general: {
        title: "ATK Tejo Ult A-Rush",
        paragraphs: [
          "Overview video for the A-Rush Tejo ultimate variation."
        ]
      }
    }
  },
  "atk-tejo-ult-b-rush": {
    "atk-tejo-ult-b-rush-overview": {
      general: {
        title: "ATK Tejo Ult B-Rush",
        paragraphs: [
          "This B-Long Rush is good to do when the defenders to stick heavier towards A-Site or you want to go for a pace change. This round is riskier to do when there could be a potential OP B-Long on an agent with a TP ability such as Yoru or Chamber.",
          "On this B-Rush, Waylay, Skye, Tejo and Brim Hit B-Site from B-Long Smoking CT and Hokkah taking Elbow space instead of Hokkah on the hit, while Viper plays the late backstage role on Hokkah."
        ]
      },
      waylay: {
        title: "ATK Tejo Ult B-Rush",
        paragraphs: [
          "On this execute Waylay follows up the the supportive utility B-Long before dashing top Pocket Box on the site hit to capitalize on any defender trying to run off site due to the Tejo Ult. After this Waylay drops down to take the elbow space with Skye."
        ]
      },
      skye: {
        title: "ATK Tejo Ult B-Rush",
        paragraphs: [
          "On round start Skye walks up B-Fountain. As the Viper Molly lands, Skye flashes up into B-Garden. The sanken bite discourages any defender to peak from Garden, while the combination of the Skye Flash and Tejo drone should grant the attackers the B-Long and Garden space. After taking the B-Long space Skye flashes the team onto site before then taking the elbow space with Waylay."
        ]
      },
      tejo: {
        title: "ATK Tejo Ult B-Rush",
        paragraphs: [
          "On round start Tejo drones up B-Long from fountain. After the drone, Tejo double Salvo‘s Elbow and throws the Armageddon from Elbow to back site. After taking site, Tejo should plant for Elbow, while Brim holds Hokkah and Waylay and Skye take elbow. Tejo should not have to worry about back site, as the Armageddon will have cleared all of back site and the Brim Molly on the choke prevents defenders from flooding onto site."
        ]
      },
      brimstone: {
        title: "ATK Tejo Ult B-Rush",
        paragraphs: [
          "Brim starts the round of in B-Short, holding passively for any defenders push out of Hokkah. As, Viper rotates in from Market Brim rotates over to B-Long and follows up Waylay and the Util through B-Long. On the Hit Brim smokes the defenders spawn choke and Hokkah, then mollies the defenders choke and holds Hokkah for any defender pushes out of the smoke.",
          "If Viper on the late Hokkkah backstab encounters any trouble Brim should be ready to take that distraction as n opportunity to jump into Hokkah and get the trades."
        ]
      },
      viper: {
        title: "ATK Tejo Ult B-Rush",
        paragraphs: [
          "For this strat viper places a different Viper Wall. A viper wall that prevents the defenders from seeing down A-Short from Lamps and isolates on the A-Short TP. This wall is not as good as the regular wall for enabling Viper lurk towards A-Short, however, unlike the regular wall it does deny vision down A-short from site an Lamps fully without needing to invest the Viper Orb. Therefore, this Wall is perfect for this Strat, as it allows Viper to head play B-short and cycle up the Wall as the team executes up B-Long. The cycling of the wall should dissuade any defenders TP towards B-Short or t the very least force them to invest utility down A-short before feeling safe to cross the viper wall and TP. Thus, this Viper wall tends to dissuade any A-Short flank, as well as any B-Short TP both of which are dangers for Viper who is solo in this area, while the rest of the team scales up B-Long.",
          "After throwing the Wall pre-round, Viper goes heads to market to start the round of in Market form where she throws a Molly to B-Garden. After which Viper rotates over to B-Short, as Brim rotates off from B-Short. Then Viper plays the patient long game and waits for the team to execute onto B-Site before trying to late lurk through Hokkah."
        ]
      }
    }
  },
  
  "atk-b-split": {
    "atk-b-split-overview": {
      general: {
        title: "ATK B-Split",
        paragraphs: [
          "A B-Split is with Waylay and Tejo hitting site through B-Long, while Skye and Brim hit Site through Hookah.",
          "In this B-Split variation Viper is free to lurk or go with the team, as she thinks is best."
        ]
      },
      waylay: {
        title: "ATK B-Split",
        paragraphs: [
          "At the start of the round Tejo drones up B-Long. Waylay should wait until the drone is halfway up B-Long before scaling up behind it to avoid getting timing peaked before the drone gathers any info.",
          "If the drone gets broken or takes contact on anyone Waylay should invest her util and Tejo should invest a Salvo to ensure the taking of the Garden and Long space."
        ],
        subSections: [
          {
            heading: "Waylay and Tejo encounter immediate B-Long pressure with Viper A-Short",
            paragraphs: [
              "In the event of Waylay and Tejo encountering immediate B-Long pressure, e.g. through a deep B-Long smoke or the Tejo drone being broken instantly (indicating that there are likely 2 defenders B-Long).",
              "It is best for Waylay and Tejo to slow their B-Long take for a moment and ask for Viper to reposition into Market and throw a supportive B-Long Snake Bite before Waylay and Tejo send their second wave of utility in combination with sed Snake Bite to re-attempt the B-Long take."
            ]
          },
          {
            heading: "Site-Hit",
            paragraphs: [
              "If Waylay still has her dashes for the Site hit she had a few pathing options:"
            ],
            bullets: [
              "Top Pocket - A good option if Waylay no longer has her refract",
              "Top Green Box - A more aggressive and hard to perform entry that allows for deeper scaling and Waylay to capitalize on the back site players more easily. This is best done in combination with throwing a Saturate back site before dashing out. However, this pathing also leaves the Waylay more exposed and thus should only be done with a refract up.",
              "Left corner of front Tube - A good safe dash option for her if Waylay has no refract or Saturate left anymore. After dashing here, Waylay need to wait for her team to scale out of Long and Hokkah before then hard clearing cubby from front site and going top Tub to fight back site."
            ]
          }
        ],
        endingParagraphs: [
          "It’s important that Waylay calls her pathing before dashing out as best as possible so the Iniator know how to best support the entry."
        ]
      },
      skye: {
        title: "ATK B-Split",
        paragraphs: [
          "At the start of the round Skye dogs Hokkah from the safe corner of Sand for Brim to follow up."
        ],
        endingParagraphs: [
          "Afterwards, Skye flashes out of Hokkah for the site take."
        ]
      },
      tejo: {
        title: "ATK B-Split",
        paragraphs: [
          "At the start of the round Tejo drones up B-Long. Waylay should wait until the drone is halfway up B-Long before scaling up behind it to avoid getting timing peaked before the drone gathers any info.",
          "If the drone gets broken or takes contact on anyone Waylay should invest her util and Tejo should invest a Salvo to ensure the taking of the Garden and Long space."
        ],
        subSections: [
          {
            heading: "Waylay and Tejo encounter immediate B-Long pressure with Viper A-Short",
            paragraphs: [
              "In the event of Waylay and Tejo encountering immediate B-Long pressure, e.g. through a deep B-Long smoke or the Tejo drone being broken instantly (indicating that there are likely 2 defenders B-Long).",
              "It is best for Waylay and Tejo to slow their B-Long take for a moment and ask for Viper to reposition into Market and throw a supportive B-Long Snake Bite before Waylay and Tejo send their second wave of utility in combination with sed Snake Bite to re-attempt the B-Long take."
            ]
          },
          {
            heading: "Site-Hit",
            paragraphs: [
              "On the Site hit, Tejo should typically Salvo Cubby. Alternatively a Salvo Back-Site or in Tube can also be very strong."
            ]
          }
        ]
      },
      brimstone: {
        title: "ATK B-Split",
        paragraphs: [
          "Brim starts the round B-Short and follows up the Skye dog to take Holkah.",
          "Then smoking elbow and defenders spawn for the Site execute before Mollying the defenders spawn site choke."
        ]
      },
      viper: {
        title: "ATK B-Split",
        paragraphs: [
          "Viper has the option of either lurking towards A-Short or going with the Team B-Long."
        ],
        subSections: [
          {
            heading: "Lurking A-Short",
            paragraphs: [
              "Lurking towards A-Short allows Viper to punish any defenders on A-Site who might to TP crunch B-Short. Beyond this it also allows for the team to cancel their B-Hit and TP A, if the mid round call ever calls for it."
            ]
          },
          {
            heading: "Supportive B-Garden Snake Bite",
            paragraphs: [
              "Keep in mind even if Viper wants to lurk towards A-Short Viper can offer up a B-Garden Snake Bite thrown from Market to help support the B-Long take. This is good to do when worried about the defenders fighting for the B-Long space and Viper nevertheless wanting to lurk towards A-Short.",
              "Another good example for when to throw this molly is when Waylay and Tejo encountered a deep B-Long smoke at the start of the round and then ask to slow down their B-Long take and get the supportive Viper Molly from market before droning up."
            ]
          },
          {
            heading: "Going B-Long",
            paragraphs: [
              "If Viper plans to go with the team she should orb the top of B-Long. For Waylay and herself to scale up behind when following up the Tejo drone. This is good to do when worried about a potential OP B-Long or playing against TP agents such as Chamber and Yoru who like playing on the TP side of B-Long and are hard to clear out."
            ]
          }
        ]
      }
    }
  },
};

const subsectionOptions = {
  "ffe-b-site": {
    topLevel: [
      { key: "b-start", label: "Round Start" },
      { key: "b-reaction", label: "B-Hit reactions" }
    ],
    branches: {
      "b-reaction": [
        { key: "b-site-setup", label: "Common B-Site hold setup" },
        { key: "b-refight", label: "Lane Refight" },
        { key: "b-post-refight", label: "Post-Refight" }
      ]
    }
  },
  "ffe-a-site": {
    topLevel: [
      { key: "a-early-round", label: "Early Round" },
      { key: "a-reaction", label: "A-Hit Reaction" }
    ],
    branches: {
      "a-reaction": [
        { key: "a-heavy-short", label: "Heavy A-Short Hit Reaction" }
      ]
    }
  },
  "def-a-short-push-b-hit": {
    topLevel: [
      { key: "b-hit-main", label: "General" }
    ],
    branches: {
      "b-hit-main": [
        { key: "b-hit-commit", label: "ATK react to the B-Crunch - committing B" },
        { key: "b-hit-tp", label: "ATK react to the B-Crunch - TPing A" }
      ]
    }
  },
};

const MediaStage = ({ mediaItem }) => {
  const [currentMedia, setCurrentMedia] = useState(mediaItem);
  const [nextMedia, setNextMedia] = useState(null);
  const [nextLoaded, setNextLoaded] = useState(false);
  const [nextPlaying, setNextPlaying] = useState(false);
  const [showNextLayer, setShowNextLayer] = useState(false);

  useEffect(() => {
    if (!mediaItem) return;

    if (!currentMedia) {
      setCurrentMedia(mediaItem);
      return;
    }

    if (mediaItem.src === currentMedia.src) {
      return;
    }

    setNextMedia(mediaItem);
    setNextLoaded(false);
    setNextPlaying(false);
    setShowNextLayer(false);
  }, [mediaItem, currentMedia]);

  const handleNextReady = useCallback(() => {
    setNextLoaded(true);
  }, []);

  const handleNextFirstFrame = useCallback(() => {
    setNextPlaying(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setShowNextLayer(true);
      });
    });
  }, []);

  const handleLayerTransitionEnd = useCallback(() => {
    if (!nextMedia || !showNextLayer || !nextPlaying) return;

    setCurrentMedia(nextMedia);
    setNextMedia(null);
    setNextLoaded(false);
    setNextPlaying(false);
    setShowNextLayer(false);
  }, [nextMedia, showNextLayer, nextPlaying]);

  const renderMediaNode = (item, mode = "current") => {
    if (!item) return null;

    const { type, src, label, props = {} } = item;

    if (type === "image") {
      return (
        <GuideImage
          key={src}
          className="image-hover-media"
          src={src}
          alt={label || "media"}
          draggable="false"
          onLoad={mode === "next" ? handleNextReady : undefined}
          {...props}
        />
      );
    }

    if (type === "video") {
      return (
        <div className="image-hover-media image-hover-media--video">
          <LazyVideo
            key={src}
            src={src}
            forceLoad={true}
            onReady={mode === "next" ? handleNextReady : undefined}
            onFirstFrame={mode === "next" ? handleNextFirstFrame : undefined}
            showLoadingOverlay={mode === "next" && !nextLoaded}
            {...props}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="media-stage image-hover-stage">
      {currentMedia && (
        <div className="media-stage__layer media-stage__layer--current">
          {renderMediaNode(currentMedia, "current")}
        </div>
      )}

      {nextMedia && (
        <div
          className={`media-stage__layer media-stage__layer--next ${
            showNextLayer ? "media-stage__layer--next-visible" : "media-stage__layer--next-hidden"
          }`}
          onTransitionEnd={handleLayerTransitionEnd}
        >
          {renderMediaNode(nextMedia, "next")}
        </div>
      )}
    </div>
  );
};

const LINEUP_VIDEO_MAP = {
  "bind-skye-showers-one-way-flash": {
    title: "Skye Showers One-Way Flash",
    src: "/assets/Ind-Bind/Skye/Bind-ATK-Skye-Showers-One-Way-Flash-Vertical.mp4",
    type: "video",
    props: { ui: "minimal", loop: true }
  }
};

const NRG2026BindComp = () => {
  // Team + UI state
  const [userTeamId, setUserTeamId] = useState(null);
  const { isBlurred } = useBlur();
  const [selectedImage, setSelectedImage] = useState("image1");
  const [selectedMediaBySection, setSelectedMediaBySection] = useState({});
  const [selectedSubsectionByMedia, setSelectedSubsectionByMedia] = useState({
    "ffe-b-site": "b-start",
    "ffe-a-site": "a-early-round"
  });
  const [selectedRoleBySection, setSelectedRoleBySection] = useState({});
  const [openAccordions, setOpenAccordions] = useState({});
  const [isMovementGuideOpen, setIsMovementGuideOpen] = useState(false);

  const [activeLineupVideo, setActiveLineupVideo] = useState(null);

  useAccordionDoubleClick({ openAccordions, setOpenAccordions });
  const location = useLocation();

  /**
   * ==========================================
   * Auto-expand accordions for highlights
   * ==========================================
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get("highlight");

    if (!highlightId) return;

    const accordionMap = {
      "BindNRG2026Comp-HIGHLIGHT-DEF-EXAMPLE": [
        "BindNRG2026Comp-Defense-Main",
        "def-defense-example-accordion"
      ],
      "BindNRG2026Comp-HIGHLIGHT-ATK-EXAMPLE": [
        "BindNRG2026Comp-Attack-Main",
        "atk-attack-example-accordion"
      ]
    };

    const pathsToOpen = accordionMap[highlightId];
    if (!pathsToOpen) return;

    setOpenAccordions(prev => {
      const updated = { ...prev };
      pathsToOpen.forEach(key => {
        updated[key] = true;
      });
      sessionStorage.setItem(
        "NRG2026BindAccordionState",
        JSON.stringify(updated)
      );
      return updated;
    });

    setTimeout(() => {
      const el = document.querySelector(
        `[data-container-id="${highlightId}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 500);
  }, [location.search]);

  // Hydrate accordion + media + role from sessionStorage
  useEffect(() => {
    const storedAccordionState = sessionStorage.getItem(
      "NRG2026BindAccordionState"
    );
    let parsedAccordionState = {};

    if (storedAccordionState) {
      try {
        parsedAccordionState = JSON.parse(storedAccordionState);
        setOpenAccordions(parsedAccordionState);
      } catch (e) {
        console.error("Failed to parse accordion state", e);
      }
    }

    const storedMediaState = sessionStorage.getItem("NRG2026BindMediaState");
    let parsedMediaState = {};

    if (storedMediaState) {
      try {
        parsedMediaState = JSON.parse(storedMediaState);
      } catch (e) {
        console.error("Failed to parse media state", e);
      }
    }

    const defaultMediaState = {};
    Object.keys(mediaSources).forEach((accordionId) => {
      const storedValue = parsedMediaState[accordionId];
      const firstKey = Object.keys(mediaSources[accordionId] || {})[0];

      if (storedValue && mediaSources[accordionId]?.[storedValue]) {
        defaultMediaState[accordionId] = storedValue;
      } else if (firstKey) {
        defaultMediaState[accordionId] = firstKey;
      }
    });

    if (Object.keys(defaultMediaState).length > 0) {
      setSelectedMediaBySection(defaultMediaState);
    }

    const storedRoles = sessionStorage.getItem("NRG2026BindRoleState");
    if (storedRoles) {
      try {
        setSelectedRoleBySection(JSON.parse(storedRoles));
      } catch {
        // ignore
      }
    }
  }, []);

  // Persist selectedMediaBySection
  useEffect(() => {
    sessionStorage.setItem(
      "NRG2026BindMediaState",
      JSON.stringify(selectedMediaBySection)
    );
  }, [selectedMediaBySection]);

  // Persist selectedImage
  useEffect(() => {
    sessionStorage.setItem("NRG2026BindSelectedImage", selectedImage);
  }, [selectedImage]);

  // Persist role state
  useEffect(() => {
    sessionStorage.setItem(
      "NRG2026BindRoleState",
      JSON.stringify(selectedRoleBySection)
    );
  }, [selectedRoleBySection]);

  // Fetch teamId from Firestore
  useEffect(() => {
    const fetchUserTeamId = async uid => {
      try {
        const userDocRef = doc(db, "users", uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          if (data.teamId) {
            setUserTeamId(data.teamId);
          } else {
            setUserTeamId(null);
          }
        } else {
          console.error("User document does not exist");
        }
      } catch (error) {
        console.error("Error fetching user team ID:", error);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        fetchUserTeamId(user.uid);
      } else {
        setUserTeamId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Page transition effect
  useEffect(() => {
    document.body.classList.add("no-transition");
    const pageContainer = document.querySelector(".page__container");
    pageContainer && pageContainer.classList.add("no-transition");

    const handleLoad = () => {
      document.body.classList.remove("no-transition");
      pageContainer && pageContainer.classList.remove("no-transition");
    };

    window.addEventListener("load", handleLoad);
    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  useEffect(() => {
    const imageContainers = document.querySelectorAll(".image-hover-effect");

    if (imageContainers.length === 0) return;

    const cleanupFunctions = [];

    imageContainers.forEach((container) => {
      const cleanup = applyImageZoomEffect(container);
      cleanupFunctions.push(cleanup);
    });

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup && cleanup());
    };
  }, [openAccordions, selectedMediaBySection, activeLineupVideo]);

  // Core accordion toggle logic
  const handleAccordionToggle = (accordionId, type) => {
    setOpenAccordions(prev => {
      const isOpen = !prev[accordionId];
      const updated = { ...prev, [accordionId]: isOpen };
      sessionStorage.setItem(
        "NRG2026BindAccordionState",
        JSON.stringify(updated)
      );

      // Optional: preload media when opening
      if (isOpen && mediaSources[accordionId]) {
        const entries = Object.values(mediaSources[accordionId]);
        if (entries[0]) {
          const v0 = document.createElement("video");
          v0.src = resolveGuideAssetUrl(entries[0].src);
          v0.preload = "auto";
          v0.load();
        }
        entries.slice(1).forEach(({ src }) => {
          const doMeta = () => {
            const v = document.createElement("video");
            v.src = resolveGuideAssetUrl(src);
            v.preload = "metadata";
            v.load();
          };
          if ("requestIdleCallback" in window) {
            requestIdleCallback(doMeta, { timeout: 2000 });
          } else {
            setTimeout(doMeta, 2000);
          }
        });
      }

      if (isOpen) {
        if (type === "image") {
          setSelectedImage("image1");
        }

        if (type === "media" && mediaSources[accordionId]) {
          const firstKey = Object.keys(mediaSources[accordionId])[0];
          if (firstKey) {
            setSelectedMediaBySection(prevMedia => ({
              ...prevMedia,
              [accordionId]: firstKey
            }));
          }
        }

        // Ensure a default role is selected when section opens
        const rolesForSection = SECTION_ROLES[accordionId] || ROLES;
        const defaultRole = rolesForSection[0] || "general";

        setSelectedRoleBySection(prevRoles => ({
          ...prevRoles,
          [accordionId]: prevRoles[accordionId] || defaultRole
        }));
      }

      return updated;
    });
  };


  const getDefaultMediaKey = (sectionId) => {
    const sectionMedia = mediaSources[sectionId];
    if (!sectionMedia) return null;
    const keys = Object.keys(sectionMedia);
    return keys[0] || null;
  };

  const getEffectiveSelectedMedia = (sectionId) => {
    return selectedMediaBySection[sectionId] || getDefaultMediaKey(sectionId);
  };

  const renderMediaButtons = sectionId => {
    const sectionMedia = mediaSources[sectionId];
    if (!sectionMedia) return null;

    return (
      <>
        <button
          type="button"
          onClick={() => setIsMovementGuideOpen(true)}
          className="movement-guide-question-button"
        >
          ?
        </button>
        {Object.entries(sectionMedia).map(([key, { label }]) => (
          <button
            key={key}
            onClick={() => {
              setSelectedMediaBySection(prev => ({
                ...prev,
                [sectionId]: key
              }));

              setSelectedSubsectionByMedia(prev => {
                if (key === "ffe-b-site") {
                  return { ...prev, "ffe-b-site": "b-start" };
                }
                if (key === "ffe-a-site") {
                  return { ...prev, "ffe-a-site": "a-early-round" };
                }
                return prev;
              });

              if (sectionId === "def-a-short-push" && key === "def-a-short-push-b-hit") {
                setSelectedRoleBySection(prev => ({
                  ...prev,
                  "def-a-short-push-b-hit-role": "general"
                }));
              }
            }}
            className={getEffectiveSelectedMedia(sectionId) === key ? "active" : ""}
          >
            {label}
          </button>
        ))}
      </>
    );
  };

  const getDisplayedMediaItem = (sectionId) => {
    const selectedKey = getEffectiveSelectedMedia(sectionId);
    const sectionMedia = mediaSources[sectionId];

    if (!selectedKey || !sectionMedia || !sectionMedia[selectedKey]) {
      return null;
    }

    if (sectionId === "atk-b-split" && selectedKey === "atk-b-split-overview") {
      const rawRole = selectedRoleBySection[sectionId];
      const selectedRole =
        typeof rawRole === "string" ? rawRole.trim().toLowerCase() : "general";

      switch (selectedRole) {
        case "waylay":
          return {
            label: "Overview",
            type: "video",
            src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-B-Split-Waylay.mp4",
            props: { ui: "minimal", loop: true }
          };

        case "viper":
          return {
            label: "Overview",
            type: "video",
            src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-B-Split-Viper.mp4",
            props: { ui: "minimal", loop: true }
          };

        case "general":
        case "skye":
        case "tejo":
        case "brimstone":
        default:
          return {
            label: "Overview",
            type: "video",
            src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-ATK-B-Split.mp4",
            props: { ui: "minimal", loop: true }
          };
      }
    }

    if (sectionId === "def-a-short-push" && selectedKey === "def-a-short-push-b-hit") {
      const rawRole = selectedRoleBySection["def-a-short-push-b-hit-role"];
      const selectedRole =
        typeof rawRole === "string" ? rawRole.trim().toLowerCase() : "general";

      switch (selectedRole) {
        case "b-hit-commit":
          return {
            label: "If ATK Reaction - Accelerate B-Hit",
            type: "video",
            src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-Fast-B-Commit.mp4",
            props: { ui: "minimal", loop: true }
          };

        case "b-hit-tp":
          return {
            label: "If ATK Reaction - Accelerate B-Hit",
            type: "video",
            src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-Fast-B-ShowersTP.mp4",
            props: { ui: "minimal", loop: true }
          };

        case "general":
        default:
          return {
            label: "If ATK Reaction - Accelerate B-Hit",
            type: "video",
            src: "/assets/Coordinated/Cor-Bind/NRG-2026-Bind-Comp/Bind-NRG2026Comp-DEF-A-Short-Push-Fast-B-general.mp4",
            props: { ui: "minimal", loop: true }
          };
      }
    }

    return sectionMedia[selectedKey];
  };

  const renderSelectedMedia = (sectionId) => {
    const mediaItem = getDisplayedMediaItem(sectionId);

    if (!mediaItem) {
      return null;
    }

    return <MediaStage mediaItem={mediaItem} />;
  };

  const renderSubsectionButtons = (mediaKey) => {
    const config = subsectionOptions[mediaKey];
    if (!config) return null;

    const activeKey = selectedSubsectionByMedia[mediaKey];
    const { topLevel, branches } = config;

    const renderButton = (item) => {
        const isActive = activeKey === item.key;

        return (
        <button
            key={item.key}
            type="button"
            className={isActive ? "active" : ""}
            onClick={() =>
            setSelectedSubsectionByMedia((prev) => ({
                ...prev,
                [mediaKey]: item.key
            }))
            }
        >
            {item.label}
        </button>
        );
    };

    const firstButton = topLevel[0] || null;
    const firstButtonChildren = firstButton ? branches[firstButton.key] || [] : [];
    const parentButton = topLevel[1] || null;
    const parentChildren = parentButton ? branches[parentButton.key] || [] : [];

    return (
      <div
        className={`subsection-tree ${
          mediaKey === "def-a-short-push-b-hit"
            ? "subsection-tree--def-a-short-push-b-hit"
            : ""
        }`}
      >
        {firstButton && firstButtonChildren.length === 0 && (
          <div className="role-selector subsection-tree__roundStartRow">
            {renderButton(firstButton)}
          </div>
        )}

        {firstButton && firstButtonChildren.length > 0 && (
          <div className="subsection-tree__branchBlock">
            <div className="subsection-tree__reactionConnectorRow">
              <div className="role-selector subsection-tree__reactionRow">
                {renderButton(firstButton)}
              </div>
              <div className="subsection-tree__branchTop" aria-hidden="true" />
            </div>

            <div className="subsection-tree__branchStem" aria-hidden="true" />
            <div className="subsection-tree__branchRail" aria-hidden="true" />
            <div className="role-selector subsection-tree__childrenRow">
              {firstButtonChildren.map(renderButton)}
            </div>
          </div>
        )}

        {parentButton && (
          <div className="subsection-tree__branchBlock">
            {parentChildren.length > 0 ? (
              <>
                <div className="subsection-tree__reactionConnectorRow">
                  <div className="role-selector subsection-tree__reactionRow">
                    {renderButton(parentButton)}
                  </div>
                  <div className="subsection-tree__branchTop" aria-hidden="true" />
                </div>

                <div className="subsection-tree__branchStem" aria-hidden="true" />
                <div className="subsection-tree__branchRail" aria-hidden="true" />
                <div className="role-selector subsection-tree__childrenRow">
                  {parentChildren.map(renderButton)}
                </div>
              </>
            ) : (
              <div className="role-selector subsection-tree__reactionRow">
                {renderButton(parentButton)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFightingForExtremitiesText = () => {
    const selectedKey =
      getEffectiveSelectedMedia("def-fighting-for-extremities-default-setup");

    if (!selectedKey) {
        return (
        <p className="text-base">
            Select a media option above to see detailed notes.
        </p>
        );
    }

    if (selectedKey === "ffe-overview") {
        return (
        <>
            <h3 className="h-title-md">Overview</h3>
            <p className="text-base">
            Viper & Skye play B while Waylay, while Tejo fight for showers and Brim spots A-short.
            </p>
        </>
        );
    }

    if (selectedKey === "ffe-b-site") {
        const selectedSubsection =
            selectedSubsectionByMedia["ffe-b-site"] || "b-start";

        return (
            <>
            <h3 className="h-title-md">B-Site Protocols</h3>
            {renderSubsectionButtons("ffe-b-site")}

            {selectedSubsection === "b-start" && (
                <>
                <h4 className="h-label">On Round Start</h4>
                <p className="text-base">
                    On B-site Skye starts the round in Garden, from here Skye can gather info B-long with jiggles and util, as well as support showers through tp.
                </p>

                <p className="text-base">
                    Viper throws her orb B-long and then chooses a complementary angle.
                </p>

                <h4 className="h-label">Common options are:</h4>
                <div>
                    <p className="BP-1">Viper crossing over towards Tp.</p>
                    <p className="BP-1">Or Viper Playing an off angle on site spotting Hokkah.</p>
                </div>
                </>
            )}

            {selectedSubsection === "b-reaction" && (
                <>
                <h4 className="h-label">Reactions to B-Hit</h4>
                <p className="text-base">
                    In either setup, in reaction to a B-hit, Both B-players double up and try and fight for one lane (Hokah or B-long).
                </p>

                <p className="text-base">
                    If the B-players a pushed off the lane early or realize that one lane is being fought for heavier by the attack side than the other,
                    Viper & Skye should make the gut call on whether they have have the timing to refight into the other lane or need to fall back into a site setup.
                </p>
                </>
            )}

            {selectedSubsection === "b-site-setup" && (
                <>
                <h4 className="h-label">A common Site setup is</h4>
                <p className="text-base">
                    Viper playing back site and Skye playing in the elbow smoke getting ready to flash out of it on the sits hit and peaking with viper.
                </p>

                <p className="text-base">
                    Hereby it’s important to note that if the 2 B-players fall back onto site into the elbow & back site setup and the player back-site instantly dies its better for the elbow player to live and hold the elbow space for the retake than commit to the site hold, as any retake from defenders spawn becomes incredibly hard when elbow space is lost, as defenders on the retake need to worry about being peaked from hall when facing site on the retake.
                </p>
                </>
            )}

            {selectedSubsection === "b-refight" && (
                <>
                    <h4 className="h-label">Refight</h4>
                    <p className="text-base">
                    However, as mentioned previously the B-players do not always need to fall back onto site but can instead also decide to refight for the lane, that’s being less heavily contested by the attackers. The option to refight into the other lane typically depends on:
                    </p>

                    <div>
                    <p className="BP-1">Do we know what lane is being fought for way heavier by the attackers</p>
                    <p className="BP-1">Are the B-players in positions to take the timing to Jump back into hookah and refight it</p>
                    <p className="BP-1">Can the B-players even play site or is it for example getting ult dumped?</p>
                    </div>

                    <p className="text-base">
                    Examples of common scenarios that warrant the instant giving up B-long space and immediate refighting into Hookah in this setup, are:
                    </p>

                    <div>
                    <p className="BP-1">Skye spotting 4 players near B-long a jiggle</p>
                    <p className="BP-1">Or Skye early on be forced out of Garden with util.</p>
                    </div>

                    <p className="text-base">
                    These refight's into hookah by Skye & Viper should be supported by the Flash through their own Viper wall.
                    </p>

                    <p className="text-base">
                    The Viper wall being up grants the B-players cover from the attackers rushing up long, as well as allows the B-players to jump back into hokkak behind its cover. The use of the Skye flash then increases Skye‘s and Viper’s chances of winning the fight in hokkah in case of any attackers having already scaled into hokkah. Additionally, as the viper wall blocks the line of sight from site into hookah, it then later also provides Skye and Viper cover from any attackers on site (who scaled through long) while fighting the hookah players.
                    </p>
                </>
            )}
            {selectedSubsection === "b-post-refight" && (
                <>
                    <h4 className="h-label">Post-Refight</h4>
                    <p className="text-base">
                    After refighting into Hokkah Skye and Viper need to care for
                    </p>

                    <p className="text-base">
                    In response to the defenders relight into hookah, the attackers common responses are to either crunch hokkah, from site nd B-short or flipp the map.
                    </p>

                    <p className="text-base">
                    This is why, it’s important for the first A-Site rotator to fill in the elbow space upon rotting to get the info on whether the attackers are trying flip the map and try and hold elbows space or the retake so, as to be able to crunch the attackers in the retake from multiple lanes.
                    </p>

                    <p className="text-base">
                    In order to prevent being crunched from site and B-short the defenders who refought into hokkah should not just remain in hokkah after the attackers took site from B-Long but instead should keep fighting the known weak side together to clear out any potential late B-short lurk trying to crunch hokkah with the attackers site.
                    </p>

                    <p className="text-base">
                    After doing so at least one of the defenders should keep pushing to wrap B-Long for the retake. If the attackers have already re-cleared, hokkah in the time that Skye and Viper walked B-short clearing for any late player, then both should wrap B-long for the retake and just give the hokkah space.
                    </p>

                    <p className="text-base">
                    All of these defender B-site reactions outlined, align exactly with the maps macro of playing the push and pull game to ensure that the defenders always have control as many lanes as possible for the retake to be able to crunch the attackers on the retake.
                    </p>
                </>
            )}
            </>
        );
      }

    if (selectedKey === "ffe-a-site") {
        const selectedSubsection =
            selectedSubsectionByMedia["ffe-a-site"] || "a-early-round";

        return (
        <>
            <h3 className="h-title-md">A-Site Protocols</h3>
            {renderSubsectionButtons("ffe-a-site")}

            {selectedSubsection === "a-early-round" && (
            <>
                <h4 className="h-label">Early Round</h4>
                <p className="text-base">
                Walay crosses showers for Waylay & Tejo to play off each other.
                </p>

                <p className="text-base">This can be done...</p>

                <div>
                <p className="BP-1">via putting the Viper wall up at round start for the cross</p>
                <p className="BP-1">OR by Skye flashing out of the TP for Waylay to fight A-lobby with and cross showers.</p>
                </div>
            </>
            )}

            {selectedSubsection === "a-reaction" && (
            <>
                <h4 className="h-label">Reaction to A-Hit</h4>

                <h4 className="h-label">Brim's Job</h4>
                <p className="text-base">
                In reaction to the A-hit and the attackers coming out on site, Brim falls back and gives the smoke on default.
                </p>

                <p className="text-base">
                This smoke sections of the attackers executing onto site from A-short making it hard for the attackers to fight for bench and back site, while simultaneously allowing for Brim and the showers players to hold a crossfire on the smoke and making hard for the attackers to get spike down as the defenders can use the cover of the smoke to spray down the planter off the noise of the plant tap.
                </p>

                <p className="text-base">
                After the smoke is down, the defenders should then deny the plant, via Brim molly, Tejo salvos, Cross map Viper molly and spraying through the smoke.
                </p>

                <h4 className="h-label">What the A-Site players need to call</h4>
                <p className="text-base">
                Hereby, it’s important that the A-site players call:
                </p>

                <div>
                <p className="BP-1">when the attackers are out site</p>
                <p className="BP-1">Who throws their plant denial utility first, as to not waste it</p>
                <p className="BP-1">Call to combo their utility as well. E.g Tejo stun plus brim molly or Waylays Saturate and Salvo‘s.</p>
                </div>

                <h4 className="h-label">Walay's /B-Long Players Job</h4>
                <p className="text-base">
                Meanwhile, its Waylay‘s job in showers to hold the showers space and call for the timing of the B-long players TP, so that the defender tping from B-Long and Waylay can crunch the attackers trying to fight into showers from A-lobby.
                Good practices hereby for walay are to play different angles each time in showers and off a perceived A-hit giving the B-long players a heads up to get ready to tp.
                and then throwing the Walay Saturate in the showers choke on the attackers execute. Off this, the defender B-long should Tp to crunch showers with Waylay.
                </p>

                <p className="text-base">
                It’s important to note that the standard rule is that until spike has been planted: „when defenders tp from one site to another at least one player must remain on the site that the tp is taken from.“ (As otherwise it gives away the position of all defenders and allows for the attackers to take the opposite tp and gain a free site).
                </p>

                <p className="text-base">
                Meaning that in this setup only 1 of the two B-players should take the tp to crunch showers with waylay. Putting up the Viper wall before tping, is helpful, as it covers the defenders back from A-lobby when fighting into showers.
                </p>

                <p className="text-base">
                If the defenders are worried about a player A-lobby holding for the tp, a skye flash through your own viper wall showers or a skye dog to support the viper tping can be useful in order to asses on whether that is a threat or not.
                </p>

                <h4 className="h-label">Tejo's Job</h4>
                <p className="text-base">
                In this setup Tejo‘s primary focus changes depending on where the main pressure is coming from.
                If the main pressure is coming from A-Short then Tejo should focus more on site and playing with brim to deny the plant, as well as care for the attackers push through the smoke default. Thereby, allowing waylay to solely focus on Showers and rely on the B-long player who TPs for support.
                If the main pressure seems to be coming towards showers, then Tejo should focus on supporting the fight towards showers instead and leave it up to brim to prevent the attackers who are out on site from crunching showers.
                </p>
            </>
            )}

            {selectedSubsection === "a-heavy-short" && (
            <>
                <h4 className="h-label">Heavy A-Short hit reaction</h4>
                <p className="text-base">
                In the event that it seems like full brunt of the A-hit is coming through A-Short, meaning 4+ players seen short, then its good for Walay and the B-long support to actively seek out any potential solo attacker A-lobby trying to go for a late lurk showers. Clearing the space for any late lurk, is best done by either using a Skye dog through the TP B-Long or by having Waylay and the defender who TP‘ed from B-long contacting the space together.
                </p>

                <p className="text-base">
                Once the space is cleared, waylay and any potential second player who is with her can decide to either wraparound-short or to simply keep the showers space and support the site hold.
                </p>

                <p className="text-base">
                The rule of thumb is that if the defender has pushed beyond the Showers TP, then the defender should keep wrapping towards A-Short unless spotted.
                </p>

                <p className="text-base">
                Mid round events that encourage the A-Short wrap by Waylay and the defender having TP‘d showers include:
                </p>

                <div>
                <p className="BP-1">the defenders loss of back site control</p>
                <p className="BP-1">The attackers having crunched into showers from site</p>
                </div>
            </>
            )}
        </>
        );
    }

    return null;
  };

  const renderDefAShortPushText = () => {

      const selectedKey =
        getEffectiveSelectedMedia("def-a-short-push");

      if (!selectedKey) {
        return <p className="text-base">Select a media option above.</p>;
      }

      if (selectedKey === "def-a-short-push-overview") {
        return renderDetailedSectionText("def-a-short-push");
      }

      /* ----------------------------
        Accelerate Showers
      -----------------------------*/

      if (selectedKey === "def-a-short-push-showers") {
        return (
          <>
            <h3 className="h-title-md">If Attackers Reaction Fast A-Showers</h3>

            <p className="text-base">
              If the attackers react to the A-Short push by accelerating their Showers push, then after the A-Short fight has ended, Waylay and Skye should immediately turn around and starts wrapping Showers.
            </p>

            <p className="text-base">
              Meanwhile, Tejo should turn around to setup a crossfire with Brim and play off each other to stall for Waylay and Skye‘s lurk to come in.
            </p>

            <p className="text-base">
              In the meantime Viper rotates through defenders spawn.
            </p>

            <p className="text-base">
              Hereby, its important for Viper to keep in mind that a common reaction by the attackers to this early A-Short fight and especially once realizing that they are getting wrapped showers is to try and flip the map and take the defenders spawn space.
            </p>

            <p className="text-base">
              Thus, seeing as Viper is the solo player in the defenders spawn rotate its important for Viper to play her rotate slow and more as a back stab role, expecting for an attacker to already be pushed up spawn, pipes or heaven holding for Viper.
            </p>
          </>
        );
      }

      /* ----------------------------
        Accelerate B Hit
      -----------------------------*/

      if (selectedKey === "def-a-short-push-b-hit") {
        const selectedSubsection =
          selectedRoleBySection["def-a-short-push-b-hit-role"] || "general";
        return (
          <>
            <h3 className="h-title-md">If Attackers Reaction - Accelerate B-Hit</h3>

            <div className="role-selector">
              {DEF_A_SHORT_PUSH_B_HIT_ROLES.map(roleKey => {
                const isActive =
                  (selectedRoleBySection["def-a-short-push-b-hit-role"] || "general") === roleKey;

                return (
                  <button
                    key={roleKey}
                    type="button"
                    className={isActive ? "active" : ""}
                    onClick={() =>
                      setSelectedRoleBySection(prev => ({
                        ...prev,
                        "def-a-short-push-b-hit-role": roleKey
                      }))
                    }
                  >
                    {ROLE_LABELS[roleKey]}
                  </button>
                );
              })}
            </div>

            {selectedSubsection === "general" && (
              <>
                <p className="text-base">
                  If the attackers react to the A-Short push by accelerating their B-Hit, then Waylay and Skye should keep fighting through market and get ready to crunch B-Short with Viper from Hokkah and Brim TPing from A-Short. Meanwhile, Tejo should remain A-Short as the A-Site anchor and hold for any attacker lurking up from Sign side trying to back stab Waylay and Skye after the A-Short fight.
                </p>

                <p className="text-base">
                  In response to this B-Short crunch the attackers then have two common reactions:
                </p>

                <p className="BP-1">
                  spending up the B-Long and taking B-Site, typically flipping the Map (planting back site and taking elbow and defenders spawn).
                </p>

                <p className="BP-1">
                  Taking the B-Long TP and transitioning into an A-Hit.
                </p>
              </>
            )}

            {selectedSubsection === "b-hit-commit" && (
              <>
                <h4 className="h-label">
                  Attackers react to the B-Crunch by committing B through B-Long
                </h4>

                <p className="text-base">
                  If the attackers commit to B-Site, two of the four players B-Short should wrap B-Long for the retake (preferably Waylay and Skye).
                </p>

                <p className="text-base">
                  Meanwhile, Tejo should rotate through defenders spawn for the retake caring for a potential late showers lurk while leaving A-Site. Its important for Tejo to play his defenders spawn rotate slow, carefully, and more as a backstab, as he is the solo player on this retake and the attackers are likely to have flipped the map and and be looking to kill the solo defender spawn. As, killing the solo defender spawn would allow them to fully focus their full attention on Hokkah and B-Long.
                </p>

                <p className="text-base">
                  Then when all defenders are i position and ready, the retake can commence and optimally suffocating the attackers on site, as the defence retakes from B-Long and Garden and Tejo plays the late defenders spawn backstab. Viper‘s Poison Cloud on site, can also be put up on the retake to help defenders Jump out of Hokkah.
                </p>
              </>
            )}

            {selectedSubsection === "b-hit-tp" && (
              <>
                <h4 className="h-label">
                  Attackers react to the B-Crunch by TPing Showers
                </h4>

                <p className="text-base">
                  f the attackers react to the B-Short Crunch by TPing Showers, Waylay and Skye should rotate back A and wrap showers. Hereby, Waylay and Skye need to be aware that if multiple attackers TP‘d showers its common for one of them to try and rewrap A-short and walk into them.
                </p>

                <p className="text-base">
                  Meanwhile, Tejo should react to these TP‘s by refocusing his attention from A-Short onto Site. Hereby, it is important for Tejo to be aware that a common reason for why the attackers B-Long feel comfortable taking the Teleporter towards showers is because they have a lurker on A-Site who took space.
                </p>

                <p className="text-base">
                  Brim and Viper should react to the attackers TPing showers by rotating through defenders spawn.
                </p>
              </>
            )}
          </>
        );
      }

      /* ----------------------------
        No Fast Response
      -----------------------------*/

      if (selectedKey === "def-a-short-push-no-response") {
        return (
          <>
            <h3 className="h-title-md">If Attackers Don‘t react to the A-Short Push</h3>

            <p className="text-base">
              If Attackers don‘t react to the Defenders A-Short push by accelerating their hit anywhere on the map, its likely they were setup for a default and want to re-clear the space before hitting any site. Thus, Waylay and Skye should fall back after the fight and Brim should smoke deep A-Short (forcing any attackers on the re-clear to invest utility to re-take that A-Short space).
            </p>

            <p className="text-base">
              Then Tejo should immediately drone Waylay into Showers, in order for them refight for the space and gather more info. Meanwhile, Skye and Brim should gamble their rotates the site they are expecting the attackers to end at.
            </p>
          </>
        );
      }

    };


    const renderDetailedSectionText = sectionId => {
      const selectedKey = getEffectiveSelectedMedia(sectionId);
      const roleKeys = SECTION_ROLES[sectionId] || ["general"];
      const selectedRole = selectedRoleBySection[sectionId] || roleKeys[0] || "general";

      if (!selectedKey) {
        return (
          <p className="text-base">
            Select a media option above to see detailed notes.
          </p>
        );
      }

      const sectionContent = detailedSectionContent[sectionId];
      const mediaContent = sectionContent?.[selectedKey];
      const roleContent =
        mediaContent?.[selectedRole] ||
        mediaContent?.general;

      if (
        sectionId === "atk-a-rush-heavy-a-short" &&
        selectedKey === "atk-a-rush-heavy-a-short-overview" &&
        selectedRole === "skye"
      ) {
        return (
          <>
            <h3 className="h-title-md">ATK A-Rush Heavy A-Short</h3>

            <p className="text-base">
              On round start Skye throws the{" "}
              {renderLineupTextTrigger(
                "one-way flash",
                "bind-skye-showers-one-way-flash"
              )}{" "}
              into showers for herself and Viper to follow up.
            </p>

            <p className="text-base">
              With the Skye flash and Tejo Salvo in most cases it should be quite simple for Skye & Viper to take showers. However, if they get stalled out they should ask for the A-short players to crunch showers from Site.
            </p>

            <p className="text-base">
              After Syke and Viper have dice take the showers space, Skye should dog onto site for the execute.
            </p>
          </>
        );
      }

      if (
        sectionId === "atk-a-rush-heavy-showers" &&
        selectedKey === "atk-a-rush-heavy-showers-overview" &&
        selectedRole === "skye"
      ) {
        return (
          <>
            <h3 className="h-title-md">ATK A-Rush Heavy Showers</h3>

            <p className="text-base">
              On Barrier drop, Skye throws her{" "}
              {renderLineupTextTrigger(
                "one way flash",
                "bind-skye-showers-one-way-flash"
              )}{" "}
              into showers. After this Skye quickly scales up the left side of A-showers while Brim and Waylay hold her. From here Skye dogs Waylay and Brim onto site.
            </p>

            <p className="text-base">
              In the event that a defender TPs showers before Waylay, Brim and Skye get into showers, it is on Brim to hold for the defender in TP. If the three have already gotten into showers and started the Site-Hit it is on the player the furthest back to turn around and hold for the defender who TP‘d (keeping in mind this should essentially never be Waylay).
            </p>
          </>
        );
      }

      if (!roleContent) {
        return (
          <p className="text-base">
            No text content configured yet for this media option.
          </p>
        );
      }

      const paragraphs = roleContent.paragraphs || [];
      const bullets = roleContent.bullets || [];
      const bulletInsertAfterParagraph = roleContent.bulletInsertAfterParagraph;
      const subSections = roleContent.subSections || [];
      const endingParagraphs = roleContent.endingParagraphs || [];

      return (
        <>
          {roleContent.title && (
            <h3 className="h-title-md">{roleContent.title}</h3>
          )}

          {paragraphs.map((paragraph, index) => (
            <React.Fragment key={`${sectionId}-${selectedKey}-${selectedRole}-frag-${index}`}>
              <p className="text-base">
                {paragraph}
              </p>

              {bullets.length > 0 && bulletInsertAfterParagraph === index && (
                <div>
                  {bullets.map((bullet, bulletIndex) => (
                    <p
                      key={`${sectionId}-${selectedKey}-${selectedRole}-b-${bulletIndex}`}
                      className="BP-1"
                    >
                      {bullet}
                    </p>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}

          {bullets.length > 0 && bulletInsertAfterParagraph === undefined && (
            <div>
              {bullets.map((bullet, index) => (
                <p
                  key={`${sectionId}-${selectedKey}-${selectedRole}-b-${index}`}
                  className="BP-1"
                >
                  {bullet}
                </p>
              ))}
            </div>
          )}

          {subSections.map((subSection, subSectionIndex) => (
            <React.Fragment
              key={`${sectionId}-${selectedKey}-${selectedRole}-sub-${subSectionIndex}`}
            >
              {subSection.heading && (
                <h4 className="h-label">{subSection.heading}</h4>
              )}

              {(subSection.paragraphs || []).map((paragraph, paragraphIndex) => (
                <p
                  key={`${sectionId}-${selectedKey}-${selectedRole}-sub-${subSectionIndex}-p-${paragraphIndex}`}
                  className="text-base"
                >
                  {paragraph}
                </p>
              ))}

              {(subSection.bullets || []).length > 0 && (
                <div>
                  {subSection.bullets.map((bullet, bulletIndex) => (
                    <p
                      key={`${sectionId}-${selectedKey}-${selectedRole}-sub-${subSectionIndex}-b-${bulletIndex}`}
                      className="BP-1"
                    >
                      {bullet}
                    </p>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}

          {endingParagraphs.map((paragraph, index) => (
            <p
              key={`${sectionId}-${selectedKey}-${selectedRole}-ending-${index}`}
              className="text-base"
            >
              {paragraph}
            </p>
          ))}
        </>
      );
    };

    const renderStandardMediaSection = (sectionId, dataContainerId) => (
      <div
        className="accordian__content"
        data-container-id={dataContainerId}
      >
        <div className="image-selector">
          {renderMediaButtons(sectionId)}
        </div>

        <div className="retake-content__wrapper">
          <div className="image__container">
            <div
              className="Image-Border image-hover-effect"
              data-zoom-id={sectionId}
            >
              {renderSelectedMedia(sectionId)}
            </div>
          </div>

          <div className="retake-text__container">
            {renderRoleButtons(sectionId)}
            <div className="explanatory-panel">
              {renderDetailedSectionText(sectionId)}
            </div>
          </div>
        </div>
      </div>
    );

    const renderLineupTextTrigger = (label, videoKey) => (
      <button
        type="button"
        className="lineup-inline-trigger"
        onClick={() => setActiveLineupVideo(LINEUP_VIDEO_MAP[videoKey])}
      >
        {label}
      </button>
    );

    const renderLineupVideoPopup = () => {
      if (!activeLineupVideo) return null;

      return (
        <div
          className="lineup-video-popup-overlay"
          onClick={() => setActiveLineupVideo(null)}
        >
          <div
            className="lineup-video-popup-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="lineup-video-popup-header">
              <h3 className="lineup-video-popup-title">
                {activeLineupVideo.title}
              </h3>

              <button
                type="button"
                className="lineup-video-popup-close app-cancel-control"
                onClick={() => setActiveLineupVideo(null)}
                aria-label="Close lineup video"
              >
                ×
              </button>
            </div>

            <div className="lineup-video-popup-media">
              <div
                className="Image-Border image-hover-effect"
                data-zoom-id="lineup-video-popup"
              >
                <MediaStage mediaItem={activeLineupVideo} />
              </div>
            </div>
          </div>
        </div>
      );
    };

    const renderAShowersDefaultText = () => {
        const sectionId = "atk-a-showers-default-accordion";
        const selectedKey = getEffectiveSelectedMedia(sectionId);
        const selectedRole = selectedRoleBySection[sectionId] || "general";

        if (!selectedKey) {
        return (
            <p className="text-base">
            Select a media option above to see detailed notes.
            </p>
        );
        }

        if (selectedKey === "a-showers-default-overview") {
        if (selectedRole === "general") {
            return (
            <>
                <h3 className="h-title-md">Early Round (Phase 1):</h3>

                <p className="text-base">
                This A-Showers Default, grants the team Showers space, B-fountain space and passive A-short space in early round (Phase 1), while leaving the team with a variation of Mid round options (Phase 2), such as:
                </p>

                <div>
                <p className="BP-1">A-Split Rush</p>
                <p className="BP-1">Cut Noise into A-Rehit</p>
                <p className="BP-1">A-Fake into B-Hit</p>
                <p className="BP-1">Pivot into a B-Hit</p>
                <p className="BP-1">B-Fake</p>
                </div>

                <p className="text-base">
                In this default Spike should remain on the ground near Sign, allowing for either Tejo or Skye to pick up the spike later on depending on where the team plans on ending.
                </p>
            </>
            );
        }

        if (selectedRole === "waylay") {
            return (
            <>
                <h3 className="h-title-md">Early Round (Phase 1):</h3>
                <p className="text-base">
                On Round Start Waylay runs up and peaks showers with the Skye One way flash.
                </p>
            </>
            );
        }

        if (selectedRole === "skye") {
            return (
            <>
                <h3 className="h-title-md">Early Round (Phase 1):</h3>
                <p className="text-base">
                Off barrier drop Skye throws the{" "}
                {renderLineupTextTrigger(
                  "one-way flash",
                  "bind-skye-showers-one-way-flash"
                )}{" "}
                into Showers and scales up towards showers fast.
                </p>
            </>
            );
        }

        if (selectedRole === "tejo") {
            return (
            <>
                <h3 className="h-title-md">Early Round (Phase 1):</h3>
                <p className="text-base">
                As the barrier drops Tejo Salvo's the defenders Shower entrance for Skye and Waylay before peaking cubby, to ensure no defender has the timing to cross into the A-short cubby.
                </p>
            </>
            );
        }

        if (selectedRole === "brimstone") {
            return (
            <>
                <h3 className="h-title-md">Early Round (Phase 1):</h3>
                <p className="text-base">
                Brim start's the round on the B-short Spawn barrier spotting for any early fight or walk down by the defenders out of Hokkah, allowing Viper to focus on B-Long. After 2-3 seconds, Brim is free to leave towards A-short. Most of the time,
                </p>
            </>
            );
        }

        if (selectedRole === "viper") {
            return (
            <>
                <h3 className="h-title-md">Early Round (Phase 1):</h3>
                <p className="text-base">
                Off barrier Drop, Viper throws her Poison Cloud Top B-Long and silently crosses to B-Long Jump up.
                </p>

                <p className="text-base">
                As long as Brim holds for the Hokkah push for the first 2-3 seconds of the round Viper has enough time to instantly cross to B-Long jump up before any potential silent push out of Hokkah becomes a threat.
                </p>
            </>
            );
        }
        }

        if (selectedKey === "a-showers-default-a-rush") {
        if (selectedRole === "general") {
            return (
            <>
                <h3 className="h-title-md">A-Split Rush (Phase 2):</h3>

                <p className="text-base">
                After the Phase 1 of the Default, to go into a A-Split Rush, Skye and Waylay should continues scaling Site through Showers together, while Brim comes back to A-short to join Tejo on the hit through A-short. In the meantime Viper is free to pickup any gaps and go for lurk timings.
                </p>
            </>
            );
        }

        if (selectedRole === "waylay") {
            return (
            <>
                <h3 className="h-title-md">A-Split Rush (Phase 2):</h3>
                <p className="text-base">
                In Phase 2 to transition into a straight A-Split Rush, after having taken showers space Waylay clears the deep showers angles and holds for any potential defender re-peek as Skye dogs Site before then following out the Dog onto site.
                </p>
            </>
            );
        }

        if (selectedRole === "skye") {
            return (
            <>
                <h3 className="h-title-md">A-Split Rush (Phase 2):</h3>
                <p className="text-base">
                In Phase 2 to transition into a straight A-Split Rush, after having taken showers space Skye calls she's about to take utility out and dogs, while Waylay holds for her.
                </p>
            </>
            );
        }

        if (selectedRole === "tejo") {
            return (
            <>
                <h3 className="h-title-md">A-Split Rush (Phase 2):</h3>
                <p className="text-base">
                In Phase 2 to transition into a straight A-Split Rush, Tejo waits for Brim to get back and then asks for the Viper wall up before droning up A-short and into lamps. The drone pathing should cross the Viper wall to clear pocket before cross the Viper wall again to clear into lamps.
                </p>
            </>
            );
        }

        if (selectedRole === "brimstone") {
            return (
            <>
                <h3 className="h-title-md">A-Split Rush (Phase 2):</h3>
                <p className="text-base">
                In Phase 2 to transition into a straight A-Split Rush, Brimstone places his smokes before following up the Tejo drone and taking the Lamps space for the team.
                </p>
            </>
            );
        }

        if (selectedRole === "viper") {
            return (
            <>
                <h3 className="h-title-md">A-Split Rush (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team transitions into a A-Split Rush, Viper has quite a lot of flexibility on how to proceed in phase 2 based on the information present in the round and her read.
                </p>

                <p className="text-base">
                Common actions for Viper in sed Phase 2 A-Split Rush are:
                </p>

                <div>
                <p className="BP-1">Remaining at the B-Long Jump up to hold the flank and only in the post rotate towards A, either via Spawn or B-Long TP</p>
                <p className="BP-1">Lurking Up B-Long using the Viper orb and going for a timing</p>
                <p className="BP-1">In reaction to defenders taking the B-Long TP, scaling up B-long and either calling for your team to TP towards B or also take the B-Long TP and backstab the defenders who took the tp.</p>
                <p className="BP-1">Alternatively, Viper can also cycle the Poison Cloud B-Long to dissuade any push down B-Long, using this as a kind of pseudo flank trip, while trying to go for a lurk timing in hookah.</p>
                </div>
            </>
            );
        }
        }

        if (selectedKey === "a-showers-default-cut-noise-a-rehit") {
            if (selectedRole === "general") {
                return (
                <>
                    <h3 className="h-title-md">A-Cut Noise Rehit (Phase 2):</h3>

                    <p className="text-base">
                    If the Plan for Phase 2 is to cut noise before re-hitting, its important for the 4 players towards A to not actively keep ego peaking and giving away that there are multiple towards A.
                    </p>
                </>
                );
            }

            if (selectedRole === "waylay") {
                return (
                <>
                    <h3 className="h-title-md">A-Cut Noise Rehit (Phase 2):</h3>

                    <p className="text-base">
                    If the plan for phase 2 is to cut noise before re-hitting its important for Waylay and Skye to clearly communicate on who shoots util and who holds what.
                    </p>

                    <p className="text-base">
                    A common example is that Waylay plays a deeper angle in Showers, while Skye plays an anti flash angle incase of any re-clear and Waylay calls to break any preclear util (e.g. drones/dogs)
                    </p>
                </>
                );
            }

            if (selectedRole === "skye") {
                return (
                <>
                    <h3 className="h-title-md">A-Cut Noise Rehit (Phase 2):</h3>

                    <p className="text-base">
                    If the plan for phase 2 is to cut noise before re-hitting its important for Skye and Waylay to clearly communicate on who shoots util and who holds what.
                    </p>

                    <p className="text-base">
                    A common example is that Waylay plays a deeper angle in Showers, while Skye plays an anti flash angle incase of any re-clear and Waylay calls to break any re-clear util (e.g. drones/dogs)
                    </p>
                </>
                );
            }

            if (selectedRole === "tejo") {
                return (
                <>
                    <h3 className="h-title-md">A-Cut Noise Rehit (Phase 2):</h3>

                    <p className="text-base">
                    If the plan for phase 2 is to cut noise before re-hitting Tejo and Brim should take up complementary passive angles on A-short, that allow them to hold for any defenders push down deep-A-short while also ensuring that they are not peaking into site.
                    </p>
                </>
                );
            }

            if (selectedRole === "brimstone") {
                return (
                <>
                    <h3 className="h-title-md">A-Cut Noise Rehit (Phase 2):</h3>

                    <p className="text-base">
                    If the plan for phase 2 is to cut noise before re-hitting Brim and Tejo should take up complementary passive angles on A-short, that allow them to hold for any defenders push down deep-A-short while also ensuring that they are not peaking into site.
                    </p>
                </>
                );
            }

            if (selectedRole === "viper") {
                return (
                <>
                    <h3 className="h-title-md">A-Cut Noise Rehit (Phase 2):</h3>

                    <p className="text-base">
                    If the plan for phase 2 is to cut noise before re-hitting, Viper is given a lot of freedom to do as she thinks is best with the information present in the round.
                    </p>

                    <p className="text-base">
                    Common examples are:
                    </p>

                    <div>
                    <p className="BP-1">Viper cycling her Poison Cloud B-Long and making slight noise as to keep some rotates on B, before regrouping with the team for an A-Hit</p>
                    <p className="BP-1">Viper using the Poison Cloud B-Long to try and take a timing up B-Long</p>
                    <p className="BP-1">Viper using the Poison Cloud B-Long to cross back to B-short and see if she can lurk Hokkah or create pressure Hokkah before the team re-hits A</p>
                    <p className="BP-1">Viper remaining patient on the B-Long jump up until the team hits site and then going for any of the previously mentioned options.</p>
                    </div>
                </>
                );
            }
        }

        if (selectedKey === "a-showers-default-a-fake") {
        if (selectedRole === "general") {
            return (
            <>
                <h3 className="h-title-md">A-Fake End B (Phase 2):</h3>

                <p className="text-base">
                This is a possibility of how in Phase 2 of the round the team could transition into a A-Fake with Ending B. Hereby, Tejo and Waylay Fake the Hit through A-short with a Site-Smoke, while Skye and Viper Group up B-Long scale using he Poison Cloud. Meanwhile Brim waits outside of B-short for Tejo take the TP to then clear Hookah fast together when transitioning into the B-Hit. As Waylay has a recall on this Fake Waylay ahs the liberty to decide on whether to refract out and regroup with the team or remain a nuisance on site and either go for a late backstab timing or simply just force the defenders to waste time on preclearing her.
                </p>

                <p className="text-base">
                This Fake, rarely if ever leads to a empty B-Site and that's okay. The goal of this fake, is to manipulate the rotations so that on the B-Hit, B-site is the weaker of the two defenders sites and an ability such as the brim Molly on the B-Site spawn choke gets the maximum value of denying the defenders flood onto site.
                </p>
            </>
            );
        }

        if (selectedRole === "waylay") {
            return (
            <>
                <h3 className="h-title-md">A-Fake End B (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a A-Fake and End B, then after the showers take Waylay stays showers for an extra ~8 seconds to break any potential preclear util into showers before rotating to A-short to then follow up Tejo's drone and dashing out to sell the fake.
                </p>

                <p className="text-base">
                Upon the A-Fake, Waylay has multiple scaling options such as:
                </p>

                <div>
                <p className="BP-1">following the drone up in to Lamps</p>
                <p className="BP-1">Dashing default to stay protected from Lamps and focus on the site smoke and potentially spamming through it towards any potential bench player to create pressure</p>
                <p className="BP-1">Dash directly into the Site Smoke and create pressure that way</p>
                </div>

                <p className="text-base">
                In this A fake, Waylay not only has a lot of freedom to choose where to dash but based on the information present in  the round Waylay also has the option of after the A-Fake remaining towards A-Site and going for a backstab on the defenders or can choose to simply leave and regroup with the team either via refract into A-short or simply taking the TP.
                </p>
            </>
            );
        }

        if (selectedRole === "skye") {
            return (
            <>
                <h3 className="h-title-md">A-Fake End B (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a A-Fake and End B, then after the showers take Skye rotates of and through Cave to regroup with Viper B-Long, picking up spike along the way.
                </p>

                <p className="text-base">
                As the A-Fake is occurring Skye and Viper walk up B-Long behind the Poison Cloud and get ready to fight out of it with a Skye flash.
                </p>

                <p className="text-base">
                If the scaling between the B-Long and B-Short players is good, Skye and Viper should be fighting out of the Poison Cloud right as Tejo TPs to B-Short.
                </p>
            </>
            );
        }

        if (selectedRole === "tejo") {
            return (
            <>
                <h3 className="h-title-md">A-Fake End B (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a A-Fake and End B, then as Brim leaves Tejo to go back towards A-Short Tejo ask for the viper wall to briefly come up to eb able to safely cross to box before asking for it to be dropped to eb able to hard clear A-cubby.
                </p>

                <p className="text-base">
                Then as Waylay join Tejo A-short and the entire team is in the right positions Tejo asks for the Viper wall to briefly be put up again before droning down A-short and clearing A-Pocket, top truck and lamps on the drone pathing.
                On this fake Tejo has the liberty to also invest the second salvo for the fake, before droning based on Tejo's read in the round.
                </p>

                <p className="text-base">
                After droning Tejo then takes the TP towards B-Short and groups with Brim to clear Hokkah and Split B  with the team.
                </p>
            </>
            );
        }

        if (selectedRole === "brimstone") {
            return (
            <>
                <h3 className="h-title-md">A-Fake End B (Phase 2):</h3>
                <p className="text-base">
                If In Phase 2 the team wants to transition into a A-Fake and End B, Brim rotates back to B-short. Jiggling Sand for info to check for any defenders who might be pushing out hookah before scaling to the TP Box, where from Brim then is able to give the smoke A-site for the fake. After smoking Brim then waits for Tejo to TP to B-Short to then clear Hokkah together and go into the B-Hit. On the B-Hit is pivotal that brim throws the CT molly to deny any floods onto site.
                </p>
            </>
            );
        }

        if (selectedRole === "viper") {
            return (
            <>
                <h3 className="h-title-md">A-Fake End B (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a A-Fake and End B, Viper remains patiently on the B-Long jump up waiting for Skye to rotate over and group.
                </p>

                <p className="text-base">
                In this Phase 2 Vipers Poison fuel management is key, as Viper needs to manage the fuel to be able to wall up briefly for Tejo to scale to the A-short Box, then later Wall up again briefly on the A-Fake before then dropping the wall and be able to put the Poison Cloud B-Long up for long enough for Skye and Viper to eb able to scale up into it and fight through it with a flash.
                </p>

                <p className="text-base">
                To be able to do all this its important that Viper does not leave the Viper Wall A-short up for long in both instances and is on the same page with Tejo on when the wall needs to go up and when its dropping.
                </p>

                <p className="text-base">
                If the scaling between the B-Long and B-Short players is good, Viper and Skye should be fighting out of the Poison Cloud right as Tejo TPs to B-Short before then accelerating into the B-Split Hit.
                </p>
            </>
            );
        }
        }

        if (selectedKey === "a-showers-default-b-hit") {

          if (selectedRole === "general") {
            return (
              <>
                <h3 className="h-title-md">B-Hit (Phase 2)</h3>

                <p className="text-base">
                If after the Showers Default the team wants to transition into a B-Hit, 
                it is ideal for the players positioned toward A during Phase 1 to attempt 
                baiting at least one defensive smoke on the choke before silently rotating 
                toward B.
                </p>
              </>
            );
          }

          if (selectedRole === "waylay") {
            return (
              <>
                <h3 className="h-title-md">B-Hit (Phase 2)</h3>

                <p className="text-base">
                If the team transitions into a B-Hit, Waylay should try to bait a defender 
                smoke on Showers before rotating toward B-Long through Cave.
                </p>

                <p className="text-base">
                Once Waylay and Skye regroup with Viper on B-Long, and Viper has enough 
                fuel, all three contact up B-Long behind the Poison Cloud.
                </p>

                <p className="text-base">
                When the trio reaches the Poison Cloud, Skye flashes through it. If the 
                flash catches a defender, Waylay should dash into Garden to secure trades 
                and create space.
                </p>

                <p className="text-base">
                Once initial contact is broken, the team should accelerate the B-Hit.
                </p>
              </>
            );
          }

          if (selectedRole === "skye") {
            return (
              <>
                <h3 className="h-title-md">B-Hit (Phase 2)</h3>

                <p className="text-base">
                When transitioning into the B-Hit, Skye should pick up the spike from 
                Sign side and rotate through Cave toward B-Long.
                </p>

                <p className="text-base">
                Once Skye and Waylay regroup with Viper, the trio contacts up B-Long 
                behind Viper’s Poison Cloud.
                </p>

                <p className="text-base">
                Skye should flash out of the Poison Cloud to initiate the fight.
                Before throwing the flash, Skye should make a confirmation call such as:
                </p>

                <p className="BP-1">“Ready? I’m about to flash.”</p>

                <p className="text-base">
                This ensures the entire team is prepared to fight off the flash.
                Once contact is broken the B-Hit should accelerate.
                </p>
              </>
            );
          }

          if (selectedRole === "tejo") {
            return (
              <>
                <h3 className="h-title-md">B-Hit (Phase 2)</h3>

                <p className="text-base">
                When the team pivots into a B-Hit, Tejo should first try to force a 
                defender smoke toward A-Short before rotating toward B-Short.
                </p>

                <p className="text-base">
                Tejo should wait to drone into Hookah until the B-Long contact is broken. 
                Unless defenders spot players early, the drone should enter Hookah at the 
                same time Skye breaks contact B-Long with her flash.
                </p>
              </>
            );
          }

          if (selectedRole === "brimstone") {
            return (
              <>
                <h3 className="h-title-md">B-Hit (Phase 2)</h3>

                <p className="text-base">
                When the team transitions into a B-Hit, Brim should rotate toward B-Short 
                with Tejo and wait until contact is broken on B-Long.
                </p>

                <p className="text-base">
                Once Tejo drones into Hookah, Brim follows the drone and begins the 
                B-Execute with the team.
                </p>

                <p className="text-base">
                During the execute it is crucial for Brim to molly the Defender Spawn choke 
                to deny fast defender floods onto the site.
                </p>
              </>
            );
          }

          if (selectedRole === "viper") {
            return (
              <>
                <h3 className="h-title-md">B-Hit (Phase 2)</h3>

                <p className="text-base">
                Viper should wait for the A-side players to rotate toward B-Long before 
                activating the Poison Cloud to allow Waylay and Skye to contact up 
                behind it.
                </p>

                <p className="text-base">
                Once inside the Poison Cloud, Skye flashes through it and the B-Execute 
                begins.
                </p>

                <p className="text-base">
                For this play to work, Viper must ensure she has at least 
                <strong>80% fuel</strong> before the trio starts walking up B-Long.
                </p>
              </>
            );
          }
        }

        if (selectedKey === "a-showers-default-b-fake") {
        if (selectedRole === "general") {
            return (
            <>
                <h3 className="h-title-md">B-Fake into A-Hit (Phase 2):</h3>

                <p className="text-base">
                This is one option of how in Phase 2 of the round the team can transition into a B-Fake.
                This B-Fake variation is most effect if your team has run the A-Showers Default into A-Fake previously, as for the defenders this B-Fake variation looks like a variation to the previously run A-Fake ending in a B-Hit.
                </p>

                <p className="text-base">
                This B-Fake is sold using a Brim smoke, Tejo drone and Waylay taking the TP from A-Short into B and dashing into the Site smoke to create pressure.
                </p>

                <p className="text-base">
                This fake once again will likely never lead to an empty A-Site. However, it should lead the defenders rotates at least heading towards B-Site and as on this fake you get Brim inserted front U-Haul and Skye already has Showers, it means that you off the B-Fake your team is able to capitalize of the space given a lot quicker. Beyond this, the this fake also creates pressure of a player being still towards B-Site, thereby slowing the B-Site Defenders rotates as they need to be warry of that potential.
                </p>
            </>
            );
        }

        if (selectedRole === "waylay") {
            return (
            <>
                <h3 className="h-title-md">B-Fake into A-Hit (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a B-Fake and End A, then after taking the Showers Space in Phase 1, Waylay leaves Skye Showers and rotates over to A-short.
                </p>

                <p className="text-base">
                Then when the team i in position, Waylay follows up the Tejo drone from A-short that will path into the TP and clear all of Hokkah (apart from behind the Box). As, Walay follows up the drone and TP's B-Short Waylay nd Viper regroup and take the Hokkah space with the drone and Waylay dashes into the Site smoke Brim will provide to sell the fake.
                </p>

                <p className="text-base">
                    Waylay has multiple ways how she can choose to play this fake...
                </p>

                <div>
                <p className="BP-1">either place her recall A-Short before TPing, giving her just enough time to run into Hokkah and instantly dash into the site smoke before recalling and joining the rest of the team on the A-Hit.</p>
                <p className="BP-1">Or alternatively place the refract only after TPing B-short (e.g. placing it in the TP or in Hokkah), giving herself more time to remain on site before refracting out</p>
                <p className="BP-1">Waylay can also choose to remain on site and not refract out an cause issues for the B-players and thereby slowing the rotates,</p>
                </div>

                <p className="text-base">
                Dashing into the smoke gives Waylay a safe space to dash and allows Walay to fight out of the smoke in different directions afterwards and isolate her angles and in the event someone is in the smoke already as Waylay dashes in she is free to just instantly refract out.
                </p>

                <p className="text-base">
                On an Eco, a crazy variation of this Fake can be to give Waylay the spike, and ahve her tap it on B-site before refracting back out to A-short and Joining the team in the A-Hit.
                </p>
            </>
            );
        }

        if (selectedRole === "skye") {
            return (
            <>
                <h3 className="h-title-md">B-Fake into A-Hit (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a B-Fake and End A, Skye is left solo in showers, herbey being the weak end in this setup.
                </p>

                <p className="text-base">
                This means Skye should play more passively and try and play her life in the case of a heavy showers preclear. Good options are playing pixel angles or slight off angles that allow Skye to fall back out of showers upon to much pressure.
                </p>

                <p className="text-base">
                Once the B-Fake has been executed Skye scaled out site crunching site with Brim who was deposited from U-Haul and Tejo who will come through A-short.
                </p>
            </>
            );
        }

        if (selectedRole === "tejo") {
            return (
            <>
                <h3 className="h-title-md">B-Fake into A-Hit (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a B-Fake and End A, Tejo again asks for the Viper Wall to be put up briefly A-short again  to be able to cross to the A-short box with it and hard clear A-Cubby.
                </p>

                <p className="text-base">
                Then once Waylay, Brim and Viper are in position Tejo asks for the Viper wall up again before droning front U-Haul and into the TP and popping the drone as it gets into Hokkah (clearing all Hokkah space but behind the Box). To have the time to clear all this the drone pathing here is extremely important. The drone should not clear A-pocket as here its more important that it stays alive to clear the other space than getting info on pocket, instead in should scale up from above the A-short box behind the viper wall clear the close left angle of front U-Haul before immediately turning around and going through the TP and going into Hokkah where after passing the choke it should be popped before running out of time.
                </p>

                <p className="text-base">
                Brim will follow this drone up to get inserted in front U-Haul, and Waylay will be the one following the drone up through the TP and into Hokkah.
                </p>

                <p className="text-base">
                Then once the B-fake has been executed with Walay dashing out site, Tejo hits A-site with Brim and Skye.
                </p>
            </>
            );
        }

        if (selectedRole === "brimstone") {
            return (
            <>
                <h3 className="h-title-md">B-Fake into A-Hit (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a B-Fake and End A, Brim remains A-Short with Tejo and should not give away his position.
                </p>

                <p className="text-base">
                Then as Waylay joins Brim and Tejo A-Short, Tejo will drone up A-short clearing front U-Haul before seeing the drone through TP for Waylay to follow up. Brim should silently follow up this drone and use it to get inserted front U-Haul.
                </p>

                <p className="text-base">
                After getting inserted Brim should instantly send the fake Smoke for Waylay B before pulling out his gun again and caring for any defenders peaking out of Lamps after hearing Waylay TP.
                </p>

                <p className="text-base">
                Then as Waylay fakes B, Brim is in a prime position to hear any defenders rotating of A-Site.
                Brim should feel free to go for a timing here if he feels he's got one and on the A-hit then take lamps control.
                </p>
            </>
            );
        }

        if (selectedRole === "viper") {
            return (
            <>
                <h3 className="h-title-md">B-Fake into A-Hit (Phase 2):</h3>

                <p className="text-base">
                If In Phase 2 the team wants to transition into a B-Fake and End A, Viper should briefly cycle her Poison Cloud B-Long to be able to safely cross over toward B-Short, and position herself near Hokkah. From here she can then follow up the Tejo drone that will come through the TP and play for trades with Waylay on the B-Fake
                </p>

                <p className="text-base">
                On the B-Fake if Viper has any Poison fuel left, she should cycle up the B-Long orb again, as it threatens attackers scaling up behind it, making it harder for the defenders to determine this as a B-Fake.
                </p>

                <p className="text-base">
                As Waylay dashes out into the Site smoke for the B-Fake Viper, should make herself known and help sell the B-Fake.
                </p>

                <p className="text-base">
                After this, Viper is free to act as she thinks is best on the information present in the round.
                </p>

                <p className="text-base">
                Typically, if Waylay instantly refracts out and back towards A-short then its better for Viper to remain towards B and see if she can find her lurk timing or keep some rotates onwards B with pressure but alternatively Viper can also just choose to go with the simpler approach of just rotating back A-short through Spawn and regrouping with the team (good option for man advantages).
                </p>
            </>
            );
        }
        }

        return null;
    };

    const renderRoleButtons = sectionId => {
        const roleKeys = SECTION_ROLES[sectionId] || ROLES;
        const currentRole = selectedRoleBySection[sectionId] || roleKeys[0] || "general";

        return (
        <div className="role-selector">
            {roleKeys.map(roleKey => {
            const label =
                ROLE_LABELS[roleKey] ||
                (roleKey === "general"
                ? "General"
                : `Agent ${roleKey.replace("agent", "")}`);

            const isActive = currentRole === roleKey;

            return (
                <button
                key={roleKey}
                type="button"
                className={isActive ? "active" : ""}
                onClick={() =>
                    setSelectedRoleBySection(prev => ({
                    ...prev,
                    [sectionId]: roleKey
                    }))
                }
                >
                {label}
                </button>
            );
            })}
        </div>
        );
    };

    return (
      <div className={`page__container ${isBlurred ? "blurred-content" : ""}`}>
        <MovementGuideModal
            isOpen={isMovementGuideOpen}
            onClose={() => setIsMovementGuideOpen(false)}
        />
        {renderLineupVideoPopup()}
        <Highlight />

        <main
            className={`Main__content__container ${
            isBlurred ? "blurred-content" : ""
            }`}
        >
            {/* BindNRG2026Comp_PAGE_TITLE → search/replace */}
            <h1 className="M-A-Title">NRG 2026 Bind Composition</h1>

            <ul className="accordian">
                {/* ==================================
                    DEFENSE MAIN (EXAMPLE)
                    ================================== */}
                <li>
                    <input
                    type="checkbox"
                    name="accordion-defense"
                    id="BindNRG2026Comp-Defense-Main"
                    checked={!!openAccordions["BindNRG2026Comp-Defense-Main"]}
                    onChange={() => handleAccordionToggle("BindNRG2026Comp-Defense-Main")}
                    />
                    <label htmlFor="BindNRG2026Comp-Defense-Main">
                    NRG 2026 Bind Comp Defense Playbook
                    </label>

                    <div className="accordian__content">
                    <ul>
                        {/* --- DEF Defense Example Sub-Accordion --- */}
                        {/* <li>
                          <input
                            type="checkbox"
                            name="Defense-sub"
                            id="def-defense-example-accordion"
                            checked={!!openAccordions["def-defense-example-accordion"]}
                            onChange={() =>
                              handleAccordionToggle("def-defense-example-accordion", "media")
                            }
                          />
                          <label htmlFor="def-defense-example-accordion">
                            DEF Defense Example Accordion
                            <Favorites
                              headingId="def-defense-example-accordion"
                              headingContent="DEF Defense Example Accordion"
                              pageLocation="/Cordinated-Play/NRG2026BindComp"
                              teamId={userTeamId}
                            />
                          </label>

                          <div
                            className="accordian__content"
                            data-container-id="BindNRG2026Comp-HIGHLIGHT-DEF-EXAMPLE"
                          >
                            <div className="image-selector">
                              {renderMediaButtons("def-defense-example-accordion")}
                            </div>

                            <div className="retake-content__wrapper">
                              <div className="image__container">
                                <div
                                  className="Image-Border image-hover-effect"
                                  data-zoom-id="def-defense-example-accordion"
                                >
                                  {renderSelectedMedia("def-defense-example-accordion")}
                                </div>
                              </div>

                              <div className="retake-text__container">
                                {renderRoleButtons("def-defense-example-accordion")}
                                <p>{getTextForSection("def-defense-example-accordion")}</p>
                              </div>
                            </div>
                          </div>
                        </li> */}
                        
                        {/* --- DEF Standard Setups --- */}
                        <li>
                          <input
                            type="checkbox"
                            name="Defense-sub"
                            id="def-standard-setups"
                            checked={!!openAccordions["def-standard-setups"]}
                            onChange={() => handleAccordionToggle("def-standard-setups")}
                          />
                          <label htmlFor="def-standard-setups">
                            DEF Standard Setups
                          </label>

                          <div className="accordian__content">
                            <ul>
                              <li>
                                <input
                                  type="checkbox"
                                  name="Defense-standard-sub"
                                  id="def-fighting-for-extremities-default-setup"
                                  checked={!!openAccordions["def-fighting-for-extremities-default-setup"]}
                                  onChange={() =>
                                    handleAccordionToggle("def-fighting-for-extremities-default-setup", "media")
                                  }
                                />
                                <label htmlFor="def-fighting-for-extremities-default-setup">
                                  DEF Fighting For Extremities Default Setup
                                  <Favorites
                                    headingId="def-fighting-for-extremities-default-setup"
                                    headingContent="DEF Fighting For Extremities Default Setup"
                                    pageLocation="/Cordinated-Play/NRG2026BindComp"
                                    teamId={userTeamId}
                                  />
                                </label>

                                <div
                                  className="accordian__content"
                                  data-container-id="NRG-BIND-FIGHTING-FOR-EXTREMITIES-DEFAULT-SETUP"
                                >
                                  <div className="image-selector">
                                    {renderMediaButtons("def-fighting-for-extremities-default-setup")}
                                  </div>

                                  <div className="retake-content__wrapper">
                                    <div className="image__container">
                                      <div
                                        className="Image-Border image-hover-effect"
                                        data-zoom-id="def-fighting-for-extremities-default-setup"
                                      >
                                        {renderSelectedMedia("def-fighting-for-extremities-default-setup")}
                                      </div>
                                    </div>

                                    <div className="retake-text__container">
                                      {renderFightingForExtremitiesText()}
                                    </div>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </li>
                        
                        {/* --- DEF Setups with Ultimates --- */}
                        <li>
                          <input
                            type="checkbox"
                            name="Defense-sub"
                            id="def-setups-with-ultimates"
                            checked={!!openAccordions["def-setups-with-ultimates"]}
                            onChange={() => handleAccordionToggle("def-setups-with-ultimates")}
                          />
                          <label htmlFor="def-setups-with-ultimates">
                            DEF Setups with Ultimates
                          </label>

                          <div className="accordian__content">
                            <ul>
                              <li>
                                <input
                                  type="checkbox"
                                  name="Defense-ult-sub"
                                  id="def-ult-setups"
                                  checked={!!openAccordions["def-ult-setups"]}
                                  onChange={() => handleAccordionToggle("def-ult-setups")}
                                />
                                <label htmlFor="def-ult-setups">
                                  DEF Ult Setups
                                </label>

                                <div className="accordian__content">
                                  <ul>
                                    <li>
                                      <input
                                        type="checkbox"
                                        name="Defense-ult-setups-sub"
                                        id="def-viper-ult-a-short"
                                        checked={!!openAccordions["def-viper-ult-a-short"]}
                                        onChange={() =>
                                          handleAccordionToggle("def-viper-ult-a-short", "media")
                                        }
                                      />
                                      <label htmlFor="def-viper-ult-a-short">
                                        DEF Viper Ult A-Short
                                        <Favorites
                                          headingId="def-viper-ult-a-short"
                                          headingContent="DEF Viper Ult A-Short"
                                          pageLocation="/Cordinated-Play/NRG2026BindComp"
                                          teamId={userTeamId}
                                        />
                                      </label>

                                      {renderStandardMediaSection(
                                        "def-viper-ult-a-short",
                                        "BindNRG2026Comp-HIGHLIGHT-DEF-VIPER-ULT-A-SHORT"
                                      )}
                                    </li>
                                  </ul>
                                </div>
                              </li>
                              <li>
                                <input
                                  type="checkbox"
                                  name="Defense-ult-sub"
                                  id="def-ult-trap-plays"
                                  checked={!!openAccordions["def-ult-trap-plays"]}
                                  onChange={() => handleAccordionToggle("def-ult-trap-plays")}
                                />
                                <label htmlFor="def-ult-trap-plays">
                                  DEF Ult Trap Plays
                                </label>

                                <div className="accordian__content">
                                  <ul>
                                    <li>
                                      <input
                                        type="checkbox"
                                        name="Defense-ult-trap-sub"
                                        id="def-b-long-trap-tejo-ult"
                                        checked={!!openAccordions["def-b-long-trap-tejo-ult"]}
                                        onChange={() =>
                                          handleAccordionToggle("def-b-long-trap-tejo-ult", "media")
                                        }
                                      />
                                      <label htmlFor="def-b-long-trap-tejo-ult">
                                        DEF B-Long Trap Tejo Ult
                                        <Favorites
                                          headingId="def-b-long-trap-tejo-ult"
                                          headingContent="DEF B-Long Trap Tejo Ult"
                                          pageLocation="/Cordinated-Play/NRG2026BindComp"
                                          teamId={userTeamId}
                                        />
                                      </label>

                                      {renderStandardMediaSection(
                                        "def-b-long-trap-tejo-ult",
                                        "BindNRG2026Comp-HIGHLIGHT-DEF-B-LONG-TRAP-TEJO-ULT"
                                      )}
                                    </li>
                                  </ul>
                                </div>
                              </li>

                              <li>
                                <input
                                  type="checkbox"
                                  name="Defense-ult-sub"
                                  id="def-ult-pushes"
                                  checked={!!openAccordions["def-ult-pushes"]}
                                  onChange={() => handleAccordionToggle("def-ult-pushes")}
                                />
                                <label htmlFor="def-ult-pushes">
                                  DEF Ult Pushes
                                </label>

                                <div className="accordian__content">
                                  <ul>
                                    <li>
                                      <input
                                        type="checkbox"
                                        name="Defense-ult-pushes-sub"
                                        id="def-ult-pushes-orbital-strike-b-long"
                                        checked={!!openAccordions["def-ult-pushes-orbital-strike-b-long"]}
                                        onChange={() =>
                                          handleAccordionToggle("def-ult-pushes-orbital-strike-b-long", "media")
                                        }
                                      />
                                      <label htmlFor="def-ult-pushes-orbital-strike-b-long">
                                        DEF Ult Pushes Orbital Strike B-Long
                                        <Favorites
                                          headingId="def-ult-pushes-orbital-strike-b-long"
                                          headingContent="DEF Ult Pushes Orbital Strike B-Long"
                                          pageLocation="/Cordinated-Play/NRG2026BindComp"
                                          teamId={userTeamId}
                                        />
                                      </label>

                                      {renderStandardMediaSection(
                                        "def-ult-pushes-orbital-strike-b-long",
                                        "BindNRG2026Comp-HIGHLIGHT-DEF-ULT-PUSHES-ORBITAL-STRIKE-B-LONG"
                                      )}
                                    </li>
                                  </ul>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </li>

                        {/* --- DEF Trap Setups --- */}
                        <li>
                          <input
                            type="checkbox"
                            name="Defense-sub"
                            id="def-trap-setups"
                            checked={!!openAccordions["def-trap-setups"]}
                            onChange={() => handleAccordionToggle("def-trap-setups")}
                          />
                          <label htmlFor="def-trap-setups">
                            DEF Trap Setups
                          </label>

                          <div className="accordian__content">
                            <ul>
                               <li>
                                <input
                                  type="checkbox"
                                  name="Trap-sub"
                                  id="def-b-long-tejo-waylay-trap"
                                  checked={!!openAccordions["def-b-long-tejo-waylay-trap"]}
                                  onChange={() =>
                                    handleAccordionToggle("def-b-long-tejo-waylay-trap", "media")
                                  }
                                />
                                <label htmlFor="def-b-long-tejo-waylay-trap">
                                  DEF B-Long Tejo/Waylay Trap
                                  <Favorites
                                    headingId="def-b-long-tejo-waylay-trap"
                                    headingContent="DEF B-Long Tejo/Waylay Trap"
                                    pageLocation="/Cordinated-Play/NRG2026BindComp"
                                    teamId={userTeamId}
                                  />
                                </label>

                                {renderStandardMediaSection(
                                  "def-b-long-tejo-waylay-trap",
                                  "BindNRG2026Comp-HIGHLIGHT-DEF-B-LONG-TEJO-WAYLAY-TRAP"
                                )}
                              </li>
                            </ul>
                          </div>
                        </li>

                        {/* --- DEF Set Pushes --- */}
                        <li>
                          <input
                            type="checkbox"
                            name="Defense-sub"
                            id="def-set-pushes"
                            checked={!!openAccordions["def-set-pushes"]}
                            onChange={() => handleAccordionToggle("def-set-pushes")}
                          />
                          <label htmlFor="def-set-pushes">
                            DEF Set Pushes'
                          </label>

                          <div className="accordian__content">
                            <ul>
                              <li>
                                <input
                                  type="checkbox"
                                  name="SetPush-sub"
                                  id="def-a-short-push"
                                  checked={!!openAccordions["def-a-short-push"]}
                                  onChange={() => handleAccordionToggle("def-a-short-push", "media")}
                                />
                                <label htmlFor="def-a-short-push">
                                  DEF A-Short Push
                                  <Favorites
                                    headingId="def-a-short-push"
                                    headingContent="DEF A-Short Push"
                                    pageLocation="/Cordinated-Play/NRG2026BindComp"
                                    teamId={userTeamId}
                                  />
                                </label>

                                <div
                                  className="accordian__content"
                                  data-container-id="BindNRG2026Comp-HIGHLIGHT-DEF-A-SHORT-PUSH"
                                >
                                  <div className="image-selector">
                                    {renderMediaButtons("def-a-short-push")}
                                  </div>

                                  <div className="retake-content__wrapper">
                                    <div className="image__container">
                                      <div
                                        className="Image-Border image-hover-effect"
                                        data-zoom-id="def-a-short-push"
                                      >
                                        {renderSelectedMedia("def-a-short-push")}
                                      </div>
                                    </div>

                                    <div className="retake-text__container">
                                      {getEffectiveSelectedMedia("def-a-short-push") === "def-a-short-push-overview" && (
                                        renderRoleButtons("def-a-short-push")
                                      )}

                                      <div className="explanatory-panel">
                                        {renderDefAShortPushText()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </li>
                    </ul>
                    </div>
                </li>

                {/* ==================================
                    ATTACK MAIN (EXAMPLE)
                    ================================== */}
                <li>
                    <input
                    type="checkbox"
                    name="accordion-attack"
                    id="BindNRG2026Comp-Attack-Main"
                    checked={!!openAccordions["BindNRG2026Comp-Attack-Main"]}
                    onChange={() => handleAccordionToggle("BindNRG2026Comp-Attack-Main")}
                    />
                    <label htmlFor="BindNRG2026Comp-Attack-Main">
                    NRG 2026 Bind Comp Attack Playbook
                    </label>

                    <div className="accordian__content">
                    <ul>
                        {/* --- ATK Attack Example Sub-Accordion --- */}
                        {/* <li>
                          <input
                            type="checkbox"
                            name="Attack-sub"
                            id="atk-attack-example-accordion"
                            checked={!!openAccordions["atk-attack-example-accordion"]}
                            onChange={() =>
                              handleAccordionToggle("atk-attack-example-accordion", "media")
                            }
                          />
                          <label htmlFor="atk-attack-example-accordion">
                            ATK Attack Example Accordion
                            <Favorites
                              headingId="atk-attack-example-accordion"
                              headingContent="ATK Attack Example Accordion"
                              pageLocation="/Cordinated-Play/NRG2026BindComp"
                              teamId={userTeamId}
                            />
                          </label>

                          <div
                            className="accordian__content"
                            data-container-id="BindNRG2026Comp-HIGHLIGHT-ATK-EXAMPLE"
                          >
                            <div className="image-selector">
                              {renderMediaButtons("atk-attack-example-accordion")}
                            </div>

                            <div className="retake-content__wrapper">
                              <div className="image__container">
                                <div
                                  className="Image-Border image-hover-effect"
                                  data-zoom-id="atk-attack-example-accordion"
                                >
                                  {renderSelectedMedia("atk-attack-example-accordion")}
                                </div>
                              </div>

                              <div className="retake-text__container">
                                {renderRoleButtons("atk-attack-example-accordion")}
                                <p>{getTextForSection("atk-attack-example-accordion")}</p>
                              </div>
                            </div>
                          </div>
                        </li> */}
                        {/* --- ATK Defaults --- */}
                        <li>
                          <input
                            type="checkbox"
                            name="Attack-sub"
                            id="atk-defaults-accordion"
                            checked={!!openAccordions["atk-defaults-accordion"]}
                            onChange={() =>
                              handleAccordionToggle("atk-defaults-accordion")
                            }
                          />
                          <label htmlFor="atk-defaults-accordion">
                            ATK Defaults
                            <Favorites
                              headingId="atk-defaults-accordion"
                              headingContent="ATK Defaults"
                              pageLocation="/Cordinated-Play/NRG2026BindComp"
                              teamId={userTeamId}
                            />
                          </label>

                          <div className="accordian__content">
                            <ul>
                              <li>
                                <input
                                  type="checkbox"
                                  name="Attack-defaults-sub"
                                  id="atk-a-showers-default-accordion"
                                  checked={!!openAccordions["atk-a-showers-default-accordion"]}
                                  onChange={() =>
                                    handleAccordionToggle("atk-a-showers-default-accordion", "media")
                                  }
                                />
                                <label htmlFor="atk-a-showers-default-accordion">
                                  ATK A-Showers Default
                                  <Favorites
                                    headingId="atk-a-showers-default-accordion"
                                    headingContent="ATK A-Showers Default"
                                    pageLocation="/Cordinated-Play/NRG2026BindComp"
                                    teamId={userTeamId}
                                  />
                                </label>

                                <div
                                  className="accordian__content"
                                  data-container-id="BindNRG2026Comp-HIGHLIGHT-ATK-A-SHOWERS-DEFAULT"
                                >
                                  <div className="image-selector">
                                    {renderMediaButtons("atk-a-showers-default-accordion")}
                                  </div>

                                  <div className="retake-content__wrapper">
                                    <div className="image__container">
                                      <div
                                        className="Image-Border image-hover-effect"
                                        data-zoom-id="atk-a-showers-default-accordion"
                                      >
                                        {renderSelectedMedia("atk-a-showers-default-accordion")}
                                      </div>
                                    </div>

                                    <div className="retake-text__container">
                                      {renderRoleButtons("atk-a-showers-default-accordion")}
                                      <div className="explanatory-panel">
                                        {renderAShowersDefaultText()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </li>
                        {/* --- ATK Rushes --- */}
                        <li>
                          <input
                            type="checkbox"
                            name="Attack-sub"
                            id="atk-rushes-accordion"
                            checked={!!openAccordions["atk-rushes-accordion"]}
                            onChange={() => handleAccordionToggle("atk-rushes-accordion")}
                          />
                          <label htmlFor="atk-rushes-accordion">
                            ATK Rushes
                          </label>

                          <div className="accordian__content">
                            <ul>
                              <li>
                                <input
                                  type="checkbox"
                                  name="Rush-sub"
                                  id="atk-a-rush"
                                  checked={!!openAccordions["atk-a-rush"]}
                                  onChange={() => handleAccordionToggle("atk-a-rush")}
                                />
                                <label htmlFor="atk-a-rush">ATK A-Rush</label>

                                <div className="accordian__content">
                                  <ul>
                                    <li>
                                      <input
                                        type="checkbox"
                                        name="ARush-sub"
                                        id="atk-a-rush-heavy-a-short"
                                        checked={!!openAccordions["atk-a-rush-heavy-a-short"]}
                                        onChange={() =>
                                          handleAccordionToggle("atk-a-rush-heavy-a-short", "media")
                                        }
                                      />
                                      <label htmlFor="atk-a-rush-heavy-a-short">
                                        ATK A-Rush Heavy A-Short
                                        <Favorites
                                          headingId="atk-a-rush-heavy-a-short"
                                          headingContent="ATK A-Rush Heavy A-Short"
                                          pageLocation="/Cordinated-Play/NRG2026BindComp"
                                          teamId={userTeamId}
                                        />
                                      </label>

                                      {renderStandardMediaSection(
                                        "atk-a-rush-heavy-a-short",
                                        "BindNRG2026Comp-HIGHLIGHT-ATK-A-RUSH-HEAVY-A-SHORT"
                                      )}
                                    </li>

                                    <li>
                                      <input
                                        type="checkbox"
                                        name="ARush-sub"
                                        id="atk-a-rush-heavy-showers"
                                        checked={!!openAccordions["atk-a-rush-heavy-showers"]}
                                        onChange={() =>
                                          handleAccordionToggle("atk-a-rush-heavy-showers", "media")
                                        }
                                      />
                                      <label htmlFor="atk-a-rush-heavy-showers">
                                        ATK A-Rush Heavy Showers
                                        <Favorites
                                          headingId="atk-a-rush-heavy-showers"
                                          headingContent="ATK A-Rush Heavy Showers"
                                          pageLocation="/Cordinated-Play/NRG2026BindComp"
                                          teamId={userTeamId}
                                        />
                                      </label>

                                      {renderStandardMediaSection(
                                        "atk-a-rush-heavy-showers",
                                        "BindNRG2026Comp-HIGHLIGHT-ATK-A-RUSH-HEAVY-SHOWERS"
                                      )}
                                    </li>
                                  </ul>
                                </div>
                              </li>

                              <li>
                                <input
                                  type="checkbox"
                                  name="Rush-sub"
                                  id="atk-b-rush"
                                  checked={!!openAccordions["atk-b-rush"]}
                                  onChange={() => handleAccordionToggle("atk-b-rush")}
                                />
                                <label htmlFor="atk-b-rush">
                                  ATK B-Rush
                                </label>

                                <div className="accordian__content">
                                  <ul>
                                    <li>
                                      <input
                                        type="checkbox"
                                        name="BRush-sub"
                                        id="atk-b-split"
                                        checked={!!openAccordions["atk-b-split"]}
                                        onChange={() =>
                                          handleAccordionToggle("atk-b-split", "media")
                                        }
                                      />
                                      <label htmlFor="atk-b-split">
                                        ATK B-Split
                                        <Favorites
                                          headingId="atk-b-split"
                                          headingContent="ATK B-Split"
                                          pageLocation="/Cordinated-Play/NRG2026BindComp"
                                          teamId={userTeamId}
                                        />
                                      </label>

                                      {renderStandardMediaSection(
                                        "atk-b-split",
                                        "BindNRG2026Comp-HIGHLIGHT-ATK-B-SPLIT"
                                      )}
                                    </li>
                                  </ul>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </li>

                        {/* --- ATK Ultimates --- */}
                        <li>
                          <input
                            type="checkbox"
                            name="Attack-sub"
                            id="atk-ultimates"
                            checked={!!openAccordions["atk-ultimates"]}
                            onChange={() => handleAccordionToggle("atk-ultimates")}
                          />
                          <label htmlFor="atk-ultimates">
                            ATK Ultimates
                          </label>

                          <div className="accordian__content">
                            <ul>
                              <li>
                                <input
                                  type="checkbox"
                                  name="Ult-sub"
                                  id="atk-tejo-ult-a-rush"
                                  checked={!!openAccordions["atk-tejo-ult-a-rush"]}
                                  onChange={() => handleAccordionToggle("atk-tejo-ult-a-rush", "media")}
                                />
                                <label htmlFor="atk-tejo-ult-a-rush">
                                  ATK Tejo Ult A-Rush
                                  <Favorites
                                    headingId="atk-tejo-ult-a-rush"
                                    headingContent="ATK Tejo Ult A-Rush"
                                    pageLocation="/Cordinated-Play/NRG2026BindComp"
                                    teamId={userTeamId}
                                  />
                                </label>

                                {renderStandardMediaSection(
                                  "atk-tejo-ult-a-rush",
                                  "BindNRG2026Comp-HIGHLIGHT-ATK-TEJO-ULT-A-RUSH"
                                )}
                              </li>

                              <li>
                                <input
                                  type="checkbox"
                                  name="Ult-sub"
                                  id="atk-tejo-ult-b-rush"
                                  checked={!!openAccordions["atk-tejo-ult-b-rush"]}
                                  onChange={() => handleAccordionToggle("atk-tejo-ult-b-rush", "media")}
                                />
                                <label htmlFor="atk-tejo-ult-b-rush">
                                  ATK Tejo Ult B-Rush
                                  <Favorites
                                    headingId="atk-tejo-ult-b-rush"
                                    headingContent="ATK Tejo Ult B-Rush"
                                    pageLocation="/Cordinated-Play/NRG2026BindComp"
                                    teamId={userTeamId}
                                  />
                                </label>

                                {renderStandardMediaSection(
                                  "atk-tejo-ult-b-rush",
                                  "BindNRG2026Comp-HIGHLIGHT-ATK-TEJO-ULT-B-RUSH"
                                )}
                              </li>
                            </ul>
                          </div>
                        </li>
                    </ul>
                    </div>
                </li>
            </ul>
        </main>

        <section className="footer-section">
            <Footer />
        </section>
        </div>
    );
};

export default NRG2026BindComp;
